import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import apiService from '../services/apiService';
import authService from '../services/authService';
import appConfig from '../config/appConfig';
import fileUploadService from '../services/fileUploadService';

const ApplicationForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [statesError, setStatesError] = useState('');
  // District states for each dropdown
  const [organizationDistricts, setOrganizationDistricts] = useState([]);
  const [organizationDistrictsLoading, setOrganizationDistrictsLoading] = useState(false);
  const [permanentDistricts, setPermanentDistricts] = useState([]);
  const [permanentDistrictsLoading, setPermanentDistrictsLoading] = useState(false);
  const [correspondenceDistricts, setCorrespondenceDistricts] = useState([]);
  const [correspondenceDistrictsLoading, setCorrespondenceDistrictsLoading] = useState(false);
  // Post office states for each dropdown
  const [permanentPostOffices, setPermanentPostOffices] = useState([]);
  const [permanentPostOfficesLoading, setPermanentPostOfficesLoading] = useState(false);
  const [correspondencePostOffices, setCorrespondencePostOffices] = useState([]);
  const [correspondencePostOfficesLoading, setCorrespondencePostOfficesLoading] = useState(false);
  // Step validation states
  const [completedSteps, setCompletedSteps] = useState([]);
  const [stepValidationLoading, setStepValidationLoading] = useState(false);
  const [step1SubmissionLoading, setStep1SubmissionLoading] = useState(false);
  // Form validation states
  const [validationErrors, setValidationErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  // Step 1 specific error state
  const [step1Error, setStep1Error] = useState('');
  // Track current step from API
  const [apiCurrentStep, setApiCurrentStep] = useState(0);
  // Payment states
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState('');
  // Step 3 submission states
  const [isStep3SubmissionLoading, setIsStep3SubmissionLoading] = useState(false);
  const [step3Error, setStep3Error] = useState('');
  // Step 4 submission states
  const [isStep4SubmissionLoading, setIsStep4SubmissionLoading] = useState(false);
  const [step4Error, setStep4Error] = useState('');
  // File upload tracking states
  const [uploadedFileNames, setUploadedFileNames] = useState({
    hs_marksheetfile: '',
    int_marksheetfile: '',
    gradu_marksheetfile: '',
    poly_marksheetfile: '',
    docup_pp_photo: '',
    docup_std_sign: '',
    docup_aadharcard: '',
    docup_experince_emp: '',
    docup_otherdoc: '',
    docup_migrate: ''
  });
  
  // File upload status and progress tracking
  const [fileUploadStatus, setFileUploadStatus] = useState({
    hs_marksheetfile: { uploading: false, uploaded: false, url: '', error: '' },
    int_marksheetfile: { uploading: false, uploaded: false, url: '', error: '' },
    gradu_marksheetfile: { uploading: false, uploaded: false, url: '', error: '' },
    poly_marksheetfile: { uploading: false, uploaded: false, url: '', error: '' },
    docup_pp_photo: { uploading: false, uploaded: false, url: '', error: '' },
    docup_std_sign: { uploading: false, uploaded: false, url: '', error: '' },
    docup_aadharcard: { uploading: false, uploaded: false, url: '', error: '' },
    docup_experince_emp: { uploading: false, uploaded: false, url: '', error: '' },
    docup_otherdoc: { uploading: false, uploaded: false, url: '', error: '' },
    docup_migrate: { uploading: false, uploaded: false, url: '', error: '' }
  });

  // Additional documents state management
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  // Academic records state
  const [academicRecords, setAcademicRecords] = useState([]);
  const [academicRecordsLoading, setAcademicRecordsLoading] = useState(false);
  const [academicRecordsError, setAcademicRecordsError] = useState('');
  // Admission sessions state
  const [admissionSessions, setAdmissionSessions] = useState([]);
  const [admissionSessionsLoading, setAdmissionSessionsLoading] = useState(false);
  const [admissionSessionsError, setAdmissionSessionsError] = useState('');
  // University courses state
  const [universityCourses, setUniversityCourses] = useState([]);
  const [universityCoursesLoading, setUniversityCoursesLoading] = useState(false);
  const [universityCoursesError, setUniversityCoursesError] = useState('');
  // Specializations state
  const [specializations, setSpecializations] = useState([]);
  const [specializationsLoading, setSpecializationsLoading] = useState(false);
  const [specializationsError, setSpecializationsError] = useState('');
  // Payment structures state
  const [paymentStructures, setPaymentStructures] = useState([]);
  const [paymentStructuresLoading, setPaymentStructuresLoading] = useState(false);
  const [paymentStructuresError, setPaymentStructuresError] = useState('');
  // Date states for datepicker
  const [dobDate, setDobDate] = useState(null);
  const [hsExamYearDate, setHsExamYearDate] = useState(null);
  const [intExamYearDate, setIntExamYearDate] = useState(null);
  const [graduExamYearDate, setGraduExamYearDate] = useState(null);
  const [polyExamYearDate, setPolyExamYearDate] = useState(null);


  const [formData, setFormData] = useState({
    // Step 1: Basic Details
    full_name: '',
    father_name: '',
    mother_name: '',
    dob: '',
    age: '',
    gender: '',
    category: '',
    aadhar_number: '',
    employment_status: '',
    name_of_organization: '',
    designation: '',
    working_from: '',
    organization_state: '',
    organization_district: '',
    linkedin_profile: '',
    email: '',
    alternate_email: '',
    phone: '',
    alternate_phone: '',
    permanent_address: '',
    permanent_pincode: '',
    permanent_city: '',
    permanent_district: '',
    permanent_state: '',
    correspondence_address: '',
    correspondence_pincode: '',
    correspondence_city: '',
    correspondence_district: '',
    correspondence_state: '',
    is_same_as_correspondence: false,
    
    // Step 2: Course & Fee
    admission_session: '',
    admission_type: 'Fresh',
    course: '',
    sub_course: '',
    semester: '1',
    fee_type: '',
    amount: '',
    
    // Step 3: Academic Details
    hs_higher_school: '',
    hs_qualification_subject: 'All Subjects',
    hs_exam_year: '',
    hs_university_board: '',
    hs_maximum_marks: '',
    hs_obtained_marks: '',
    hs_grade: '',
    hs_marksheetfile: '',
    int_intermediate: '',
    int_subject: '',
    int_exam_year: '',
    int_university_board: '',
    int_maximum_marks: '',
    int_obtained_marks: '',
    int_grade: '',
    int_marksheetfile: '',
    gradu_intermediate: '',
    gradu_course: '',
    gradu_exam_year: '',
    gradu_university_board: '',
    gradu_obtained_marks: '',
    gradu_maximum_marks: '',
    gradu_grade: '',
    gradu_marksheetfile: '',
    
    // Polytechnic Diploma / Other
    poly_intermediate: '',
    poly_course: '',
    poly_exam_year: '',
    poly_university_board: '',
    poly_obtained_marks: '',
    poly_maximum_marks: '',
    poly_grade: '',
    poly_marksheetfile: '',
    
    // Step 4: Document Upload
    docup_pp_photo: '',
    docup_std_sign: '',
    docup_aadharcard: '',
    docup_experince_emp: '',
    docup_otherdoc: '',
    docup_migrate: ''
  });

  // Helper function to validate individual fields
  const validateField = useCallback((name, value, currentFormData = formData, districtsData = { permanentDistricts, correspondenceDistricts, organizationDistricts }, postOfficesData = { permanentPostOffices, correspondencePostOffices }) => {
    const errors = {};
    
    switch (name) {
      case 'full_name':
        if (!value || value.trim().length < 2) {
          errors.full_name = 'Full name must be at least 2 characters';
        }
        break;
      case 'father_name':
        if (!value || value.trim().length < 2) {
          errors.father_name = 'Father name must be at least 2 characters';
        }
        break;
      case 'mother_name':
        if (!value || value.trim().length < 2) {
          errors.mother_name = 'Mother name must be at least 2 characters';
        }
        break;
      case 'dob':
        if (!value) {
          errors.dob = 'Date of birth is required';
        }
        break;
      case 'gender':
        if (!value) {
          errors.gender = 'Gender is required';
        }
        break;
      case 'category':
        if (!value) {
          errors.category = 'Category is required';
        }
        break;
      case 'aadhar_number':
        if (!value) {
          errors.aadhar_number = 'Aadhar number is required';
        } else if (!/^\d{12}$/.test(value)) {
          errors.aadhar_number = 'Aadhar number must be 12 digits';
        }
        break;
      case 'email':
        if (!value) {
          errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.email = 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (!value) {
          errors.phone = 'Phone number is required';
        } else if (!/^\d{10}$/.test(value)) {
          errors.phone = 'Phone number must be 10 digits';
        }
        break;
      case 'permanent_address':
        if (!value || value.trim().length < 10) {
          errors.permanent_address = 'Permanent address must be at least 10 characters';
        }
        break;
      case 'permanent_pincode':
        if (!value) {
          errors.permanent_pincode = 'Pincode is required';
        } else if (!/^\d{6}$/.test(value)) {
          errors.permanent_pincode = 'Pincode must be 6 digits';
        }
        break;
      case 'permanent_city':
        // Only require post office if pincode is provided and post offices are available
        if (currentFormData.permanent_pincode && postOfficesData.permanentPostOffices.length > 0 && !value) {
          errors.permanent_city = 'Post office is required';
        }
        break;
      case 'permanent_state':
        if (!value) {
          errors.permanent_state = 'State is required';
        }
        break;
      case 'permanent_district':
        // Only require district if state is selected and districts are available
        if (currentFormData.permanent_state && districtsData.permanentDistricts.length > 0 && !value) {
          errors.permanent_district = 'District is required';
        }
        break;
      case 'correspondence_address':
        if (!currentFormData.is_same_as_correspondence && (!value || value.trim().length < 10)) {
          errors.correspondence_address = 'Correspondence address must be at least 10 characters';
        }
        break;
      case 'correspondence_pincode':
        if (!currentFormData.is_same_as_correspondence && (!value || !/^\d{6}$/.test(value))) {
          errors.correspondence_pincode = 'Correspondence pincode must be 6 digits';
        }
        break;
      case 'correspondence_city':
        if (!currentFormData.is_same_as_correspondence && !value) {
          errors.correspondence_city = 'Correspondence post office is required';
        }
        break;
      case 'correspondence_state':
        if (!currentFormData.is_same_as_correspondence && !value) {
          errors.correspondence_state = 'Correspondence state is required';
        }
        break;
      case 'correspondence_district':
        // Only require district if not same as permanent, state is selected, and districts are available
        if (!currentFormData.is_same_as_correspondence && 
            currentFormData.correspondence_state && 
            districtsData.correspondenceDistricts.length > 0 && 
            !value) {
          errors.correspondence_district = 'Correspondence district is required';
        }
        break;
      default:
        // No validation for unknown fields
        break;
    }
    
    return errors;
  }, [formData, permanentDistricts, correspondenceDistricts, organizationDistricts, permanentPostOffices, correspondencePostOffices]);

  // Helper function to validate entire Step 1 form
  const validateStep1Form = useCallback((currentFormData = formData) => {
    const errors = {};
    // Validate all required fields
    const requiredFields = [
      'full_name', 'father_name', 'mother_name', 'dob', 'gender', 'category',
      'aadhar_number', 'email', 'phone', 'permanent_address', 'permanent_pincode',
      'permanent_city', 'permanent_state', 'permanent_district'
    ];
    
    // Add correspondence fields if not same as permanent
    if (!currentFormData.is_same_as_correspondence) {
      requiredFields.push('correspondence_address', 'correspondence_pincode', 
                         'correspondence_city', 'correspondence_state', 'correspondence_district');
    }
        
    // Validate each field
    requiredFields.forEach(field => {
      const fieldErrors = validateField(field, currentFormData[field], currentFormData, { permanentDistricts, correspondenceDistricts, organizationDistricts }, { permanentPostOffices, correspondencePostOffices });
      if (fieldErrors[field]) {
      }
      Object.assign(errors, fieldErrors);
    });
    
    // Optional validations for employment details (only validate format if provided)
    if (currentFormData.name_of_organization && currentFormData.name_of_organization.trim().length < 2) {
      errors.name_of_organization = 'Organization name must be at least 2 characters if provided';
    }
    
    if (currentFormData.designation && currentFormData.designation.trim().length < 2) {
      errors.designation = 'Designation must be at least 2 characters if provided';
    }
    
    if (currentFormData.linkedin_profile && !/^https?:\/\/.+/.test(currentFormData.linkedin_profile)) {
      errors.linkedin_profile = 'LinkedIn profile must be a valid URL if provided';
    }
    
    
    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    setIsFormValid(isValid);
    
    
    return isValid;
  }, [formData, permanentDistricts, correspondenceDistricts, organizationDistricts, permanentPostOffices, correspondencePostOffices, validateField]);



  // Fetch step1 data on component mount - wait for states to be loaded
  useEffect(() => {
    const fetchStep1Data = async () => {
      // Don't fetch step1 data until states are loaded
      if (states.length === 0) {
        return;
      }
      
      try {
        setLoading(true);
        setError('');
        
        const response = await Promise.race([
          apiService.getStudentStep1(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('API request timeout')), appConfig.api.timeout)
          )
        ]);
        
        if (response.success && response.data.status === 'success' && response.data.student) {
          const studentData = response.data.student;
          
          // Map API response to form data - work directly with IDs
          const mappedFormData = {
            // Step 1: Basic Details
            full_name: studentData.student_name || '',
            father_name: studentData.father_name || '',
            mother_name: studentData.mother_name || '',
            dob: studentData.dob || '',
            age: '', // Will be calculated separately
            gender: studentData.gender || '',
            category: studentData.category || '',
            aadhar_number: studentData.aadhar_number || '',
            employment_status: studentData.employment_detail?.employment_status || '',
            name_of_organization: studentData.employment_detail?.organization_name || '',
            designation: studentData.employment_detail?.designation || '',
            working_from: studentData.employment_detail?.working_since || '',
            organization_state: studentData.employment_detail?.state || '',
            organization_district: studentData.employment_detail?.district || '',
            linkedin_profile: studentData.employment_detail?.linkedin_profile || '',
            email: studentData.email || '',
            alternate_email: studentData.alternate_email || '',
            phone: studentData.mobile || '',
            alternate_phone: studentData.alternate_mobile || '',
            permanent_address: studentData.permanent_address?.address || '',
            permanent_pincode: studentData.permanent_address?.pincode || '',
            permanent_city: studentData.permanent_address?.post_office || '',
            permanent_district: studentData.permanent_address?.district || '',
            permanent_state: studentData.permanent_address?.state || '',
            correspondence_address: studentData.communication_address?.address || '',
            correspondence_pincode: studentData.communication_address?.pincode || '',
            correspondence_city: studentData.communication_address?.post_office || '',
            correspondence_district: studentData.communication_address?.district || '',
            correspondence_state: studentData.communication_address?.state || '',
            is_same_as_correspondence: false, // Will be set separately
            
            // Step 2: Course & Fee (pre-populate from API response)
            admission_session: studentData.session || '',
            admission_type: studentData.admission_type || 'Fresh', // Default to "Fresh" if not provided
            course: studentData.course_id || '',
            sub_course: studentData.specialization_id || '',
            semester: studentData.semester || '1',
            fee_type: studentData.fee_type || '',
            amount: studentData.amount || '',
            
            // Step 3: Academic Details (keep empty for now)
            hs_higher_school: '',
            hs_qualification_subject: '',
            hs_exam_year: '',
            hs_university_board: '',
            hs_maximum_marks: '',
            hs_obtained_marks: '',
            hs_grade: '',
            hs_marksheetfile: '',
            int_intermediate: '',
            int_subject: '',
            int_exam_year: '',
            int_university_board: '',
            int_maximum_marks: '',
            int_obtained_marks: '',
            int_grade: '',
            int_marksheetfile: '',
            gradu_intermediate: '',
            gradu_course: '',
            gradu_exam_year: '',
            gradu_university_board: '',
            gradu_obtained_marks: '',
            gradu_maximum_marks: '',
            gradu_grade: '',
            gradu_marksheetfile: '',
            
            // Polytechnic Diploma / Other
            poly_intermediate: '',
            poly_course: '',
            poly_exam_year: '',
            poly_university_board: '',
            poly_obtained_marks: '',
            poly_maximum_marks: '',
            poly_grade: '',
            poly_marksheetfile: '',
            
            // Step 4: Document Upload (keep empty for now)
            docup_pp_photo: '',
            docup_std_sign: '',
            docup_aadharcard: '',
            docup_experince_emp: '',
            docup_otherdoc: '',
            docup_migrate: ''
          };
          
          setFormData(mappedFormData);
          
          // Set date states for datepicker
          setDobDate(stringToDate(studentData.dob));
          
          // Calculate age if date of birth is available
          if (studentData.dob) {
            const age = calculateAge(studentData.dob);
            setFormData(prev => ({
              ...prev,
              age: age
            }));
          }
          
          // Note: District and post office fetching will be handled in a separate useEffect
          // after both student data and states data are loaded
          
          // Check if correspondence address is same as permanent address
          const isSameAsPermanent = 
            studentData.communication_address?.address === studentData.permanent_address?.address &&
            studentData.communication_address?.pincode === studentData.permanent_address?.pincode &&
            studentData.communication_address?.post_office === studentData.permanent_address?.post_office;
          
          if (isSameAsPermanent) {
            setFormData(prev => ({
              ...prev,
              is_same_as_correspondence: true
            }));
          }
          
          // Validate step completion
          const currentStepFromAPI = studentData.current_step || 0;
          setApiCurrentStep(currentStepFromAPI);
          const completedStepsArray = [];
          for (let i = 1; i <= currentStepFromAPI; i++) {
            completedStepsArray.push(i);
          }
          setCompletedSteps(completedStepsArray);
          
          // If Step 2 data is pre-populated, fetch related data
          if (studentData.course_id) {
            
            // Fetch specializations for the pre-selected course
            fetchCourseSpecializations(studentData.course_id);
            // Fetch payment structures for the pre-selected course
            fetchPaymentStructures(studentData.course_id);
          }
          
          // Trigger form validation after pre-filled data is loaded
          // Use setTimeout to ensure state updates are complete
          setTimeout(() => {
            validateStep1Form();
          }, 200);
          
        } else {
          setError('Failed to load student data');
          setStep1Error('Failed to load student data. Please refresh the page.');
          
          // Initialize form with empty values when no data is available
          setFormData(prev => ({
            ...prev,
            full_name: '',
            father_name: '',
            mother_name: '',
            dob: '',
            gender: '',
            category: '',
            aadhar_number: '',
            employment_status: '',
            email: '',
            phone: '',
            permanent_address: '',
            correspondence_address: ''
          }));
        }
      } catch (err) {
        console.error('Error fetching step1 data:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.status,
          response: err.response
        });
        
        // Handle specific error cases
        if (err.message === 'API request timeout') {
          setError('API request timed out. Please check if the server is running.');
          setStep1Error('API request timed out. Please check if the server is running.');
        } else if (err.message.includes('Failed to fetch')) {
          setError('Cannot connect to server. Please check if the API server is running.');
          setStep1Error('Cannot connect to server. Please check if the API server is running.');
        } else if (err.status === 401) {
          setError('Authentication failed. Please login again.');
          setStep1Error('Authentication failed. Please login again.');
        } else {
        setError('Error loading student data. Please try again.');
          setStep1Error('Error loading student data. Please refresh the page.');
        }
        
        // Initialize form with empty values when there's an error
        setFormData(prev => ({
          ...prev,
          full_name: '',
          father_name: '',
          mother_name: '',
          dob: '',
          gender: '',
          category: '',
          aadhar_number: '',
          employment_status: '',
          email: '',
          phone: '',
          permanent_address: '',
          correspondence_address: ''
        }));
      } finally {
        setLoading(false);
      }
    };

    fetchStep1Data();
  }, [states]);

  // Fetch existing documents when Step 4 is loaded
  useEffect(() => {
    if (currentStep === 4) {
      fetchExistingDocuments();
    }
  }, [currentStep]);

  // Fetch states data on component mount
  useEffect(() => {
    const fetchStatesData = async () => {
      try {
        setStatesLoading(true);
        setStatesError('');
        
        const response = await apiService.getStates();
        
        if (response.success && response.data && response.data.success && Array.isArray(response.data.data)) {
          setStates(response.data.data);
        } else {
          setStatesError('Failed to load states data');
          setStep1Error('Failed to load states data. Please refresh the page.');
        }
      } catch (err) {
        console.error('Error fetching states data:', err);
        setStatesError('Error loading states data. Please try again.');
        setStep1Error('Error loading states data. Please refresh the page.');
      } finally {
        setStatesLoading(false);
      }
    };

    fetchStatesData();
  }, []);

  // Fetch districts and post offices when both student data and states are loaded
  useEffect(() => {
    if (states.length > 0 && formData.permanent_state) {
      // Fetch districts for permanent state
      const selectedState = states.find(state => state.id.toString() === formData.permanent_state.toString());
      if (selectedState) {
        fetchDistricts(selectedState.id, 'permanent');
      }
    }
    
    if (states.length > 0 && formData.correspondence_state) {
      // Fetch districts for correspondence state
      const selectedState = states.find(state => state.id.toString() === formData.correspondence_state.toString());
      if (selectedState) {
        fetchDistricts(selectedState.id, 'correspondence');
      }
    }
    
    if (states.length > 0 && formData.organization_state) {
      // Fetch districts for organization state
      const selectedState = states.find(state => state.id.toString() === formData.organization_state.toString());
      if (selectedState) {
        fetchDistricts(selectedState.id, 'organization');
      }
    }
    
    // Fetch post offices for permanent pincode
    if (formData.permanent_pincode && formData.permanent_pincode.length === 6) {
      fetchPostOffices(formData.permanent_pincode, 'permanent');
    }
    
    // Fetch post offices for correspondence pincode
    if (formData.correspondence_pincode && formData.correspondence_pincode.length === 6) {
      fetchPostOffices(formData.correspondence_pincode, 'correspondence');
    }
  }, [states, formData.permanent_state, formData.correspondence_state, formData.organization_state, formData.permanent_pincode, formData.correspondence_pincode]);

  // Helper function to fetch admission sessions
  const fetchAdmissionSessions = async () => {
    try {
      setAdmissionSessionsLoading(true);
      setAdmissionSessionsError('');
      
      const response = await apiService.getAdmissionSessions();
      
      // API response format: { success: true, message: "...", data: { university: {...}, admission_sessions: [...] } }
      if (response.success && response.data && response.data.success && response.data.data && response.data.data.admission_sessions) {
        setAdmissionSessions(response.data.data.admission_sessions);
      } else {
        setAdmissionSessionsError('Failed to load admission sessions');
      }
    } catch (err) {
      console.error('Error fetching admission sessions:', err);
      setAdmissionSessionsError('Failed to load admission sessions');
    } finally {
      setAdmissionSessionsLoading(false);
    }
  };

  // Helper function to fetch university courses
  const fetchUniversityCourses = async () => {
    try {
      setUniversityCoursesLoading(true);
      setUniversityCoursesError('');
      
      const response = await apiService.getUniversityCourses();
      
      if (response.success && response.data && response.data.success && response.data.data && response.data.data.courses) {
        setUniversityCourses(response.data.data.courses);
      } else {
        setUniversityCoursesError('Failed to load university courses');
      }
    } catch (err) {
      console.error('Error fetching university courses:', err);
      setUniversityCoursesError('Failed to load university courses');
    } finally {
      setUniversityCoursesLoading(false);
    }
  };

  // Helper function to fetch course specializations
  const fetchCourseSpecializations = async (courseId) => {
    if (!courseId) {
      setSpecializations([]);
      return;
    }

    try {
      setSpecializationsLoading(true);
      setSpecializationsError('');
      
      const response = await apiService.getCourseSpecializations(courseId);
      
      if (response.success && response.data && response.data.success && response.data.data) {
        setSpecializations(response.data.data);
      } else {
        setSpecializationsError('Failed to load specializations');
        setSpecializations([]);
      }
    } catch (err) {
      console.error('Error fetching specializations:', err);
      setSpecializationsError('Failed to load specializations');
      setSpecializations([]);
    } finally {
      setSpecializationsLoading(false);
    }
  };

  // Helper function to fetch payment structures
  const fetchPaymentStructures = async (courseId) => {
    if (!courseId) {
      setPaymentStructures([]);
      return;
    }

    try {
      setPaymentStructuresLoading(true);
      setPaymentStructuresError('');
      
      const response = await apiService.getPaymentStructures(courseId);
      
      if (response.success && response.data && response.data.success && response.data.data && response.data.data.payment_structures) {
        setPaymentStructures(response.data.data.payment_structures);
      } else {
        setPaymentStructuresError('Failed to load payment structures');
        setPaymentStructures([]);
      }
    } catch (err) {
      console.error('Error fetching payment structures:', err);
      setPaymentStructuresError('Failed to load payment structures');
      setPaymentStructures([]);
    } finally {
      setPaymentStructuresLoading(false);
    }
  };

  // Fetch academic records
  const fetchAcademicRecords = async () => {
    try {
      setAcademicRecordsLoading(true);
      setAcademicRecordsError('');
      
      const response = await apiService.getAcademicRecords();
      
      if (response.success && response.data && response.data.success && response.data.data && response.data.data.academic_records) {
        setAcademicRecords(response.data.data.academic_records);
        // Populate form fields with existing academic records
        populateFormWithAcademicRecords(response.data.data.academic_records);
      } else {
        setAcademicRecordsError('Failed to load academic records');
        setAcademicRecords([]);
      }
    } catch (err) {
      console.error('Error fetching academic records:', err);
      setAcademicRecordsError('Failed to load academic records');
      setAcademicRecords([]);
    } finally {
      setAcademicRecordsLoading(false);
    }
  };

  // Populate form fields with existing academic records
  const populateFormWithAcademicRecords = (records) => {
    records.forEach(record => {
      switch (record.academic_level) {
        case 'ssc':
          // High School (SSC) record
          setFormData(prev => ({
            ...prev,
            hs_higher_school: record.examination,
            hs_qualification_subject: record.subject,
            hs_exam_year: record.year.toString(),
            hs_university_board: record.board_university,
            hs_obtained_marks: record.obtained_marks,
            hs_maximum_marks: record.maximum_marks,
            hs_grade: record.percentage_grade,
            hs_marksheetfile: record.marksheet_file
          }));
          // Set the Date object for DatePicker
          setHsExamYearDate(new Date(record.year, 0, 1));
          // Update file upload status
          setFileUploadStatus(prev => ({
            ...prev,
            hs_marksheetfile: {
              uploading: false,
              uploaded: true,
              url: record.marksheet_file,
              error: '',
              fileName: record.marksheet_file.split('/').pop(),
              originalName: record.marksheet_file.split('/').pop()
            }
          }));
          // Update uploaded file names
          setUploadedFileNames(prev => ({
            ...prev,
            hs_marksheetfile: record.marksheet_file.split('/').pop()
          }));
          break;
          
        case 'hsc':
          // Intermediate (HSC) record
          setFormData(prev => ({
            ...prev,
            int_intermediate: record.examination,
            int_subject: record.subject,
            int_exam_year: record.year.toString(),
            int_university_board: record.board_university,
            int_obtained_marks: record.obtained_marks,
            int_maximum_marks: record.maximum_marks,
            int_grade: record.percentage_grade,
            int_marksheetfile: record.marksheet_file
          }));
          // Set the Date object for DatePicker
          setIntExamYearDate(new Date(record.year, 0, 1));
          // Update file upload status
          setFileUploadStatus(prev => ({
            ...prev,
            int_marksheetfile: {
              uploading: false,
              uploaded: true,
              url: record.marksheet_file,
              error: '',
              fileName: record.marksheet_file.split('/').pop(),
              originalName: record.marksheet_file.split('/').pop()
            }
          }));
          // Update uploaded file names
          setUploadedFileNames(prev => ({
            ...prev,
            int_marksheetfile: record.marksheet_file.split('/').pop()
          }));
          break;
          
        case 'graduate':
          // Graduation record
          setFormData(prev => ({
            ...prev,
            gradu_course: record.examination,
            gradu_exam_year: record.year.toString(),
            gradu_university_board: record.board_university,
            gradu_obtained_marks: record.obtained_marks,
            gradu_maximum_marks: record.maximum_marks,
            gradu_grade: record.percentage_grade,
            gradu_marksheetfile: record.marksheet_file
          }));
          // Set the Date object for DatePicker
          setGraduExamYearDate(new Date(record.year, 0, 1));
          // Update file upload status
          setFileUploadStatus(prev => ({
            ...prev,
            gradu_marksheetfile: {
              uploading: false,
              uploaded: true,
              url: record.marksheet_file,
              error: '',
              fileName: record.marksheet_file.split('/').pop(),
              originalName: record.marksheet_file.split('/').pop()
            }
          }));
          // Update uploaded file names
          setUploadedFileNames(prev => ({
            ...prev,
            gradu_marksheetfile: record.marksheet_file.split('/').pop()
          }));
          break;
          
        case 'diploma':
          // Polytechnic Diploma record
          setFormData(prev => ({
            ...prev,
            poly_intermediate: record.examination,
            poly_course: record.subject,
            poly_exam_year: record.year.toString(),
            poly_university_board: record.board_university,
            poly_obtained_marks: record.obtained_marks,
            poly_maximum_marks: record.maximum_marks,
            poly_grade: record.percentage_grade,
            poly_marksheetfile: record.marksheet_file
          }));
          // Set the Date object for DatePicker
          setPolyExamYearDate(new Date(record.year, 0, 1));
          // Update file upload status
          setFileUploadStatus(prev => ({
            ...prev,
            poly_marksheetfile: {
              uploading: false,
              uploaded: true,
              url: record.marksheet_file,
              error: '',
              fileName: record.marksheet_file.split('/').pop(),
              originalName: record.marksheet_file.split('/').pop()
            }
          }));
          // Update uploaded file names
          setUploadedFileNames(prev => ({
            ...prev,
            poly_marksheetfile: record.marksheet_file.split('/').pop()
          }));
          break;
        default:
          // Handle any other academic levels if needed
          break;
      }
    });
  };

  // Fetch admission sessions and university courses when Step 2 is accessed
  useEffect(() => {
    if (currentStep === 2) {
      fetchAdmissionSessions();
      fetchUniversityCourses();
      
      // Set default admission_type to "Fresh" if it's empty
      if (!formData.admission_type || formData.admission_type.trim() === '') {
        setFormData(prev => ({
          ...prev,
          admission_type: 'Fresh'
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Fetch academic records when Step 3 is accessed
  useEffect(() => {
    if (currentStep === 3) {
      fetchAcademicRecords();
    }
  }, [currentStep]);

  // Debug button state
  useEffect(() => {
  }, [currentStep, isFormValid, stepValidationLoading, step1SubmissionLoading, completedSteps]);

  // Check if EASEBUZZ script is loaded (script is loaded in HTML head)
  // Create a promise that resolves when EASEBUZZ is available
  const easebuzzScriptPromise = useMemo(() => {
    return new Promise((resolve) => {
      // Check if script already exists
      if (window.EasebuzzCheckout) {
        resolve(true);
        return;
      }
      
      // Wait for script to load from HTML head
      let checkCount = 0;
      const maxChecks = 50; // Check for 5 seconds (50 * 100ms)
      
      const checkInterval = setInterval(() => {
        checkCount++;
        if (window.EasebuzzCheckout) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (checkCount >= maxChecks) {
          clearInterval(checkInterval);
          console.error('EASEBUZZ script not loaded after timeout');
          resolve(false);
        }
      }, 100); // Check every 100ms
    });
  }, []);

  // EASEBUZZ payment functions
  const initiateEasebuzzPayment = async (paymentData) => {
    try {
      setIsPaymentLoading(true);
      setPaymentError('');
      setPaymentSuccess('');
      
      // Call EASEBUZZ initiate API to get access token first
      // (API call doesn't require the script to be loaded)
      const response = await apiService.initiateEasebuzzPayment(paymentData);
      console.log('EASEBUZZ initiateEasebuzzPayment response:', response);
      if (response.success && response.data && response.data.success) {
        const { access_token, order_id, amount, txnid, merchant_key } = response.data.data;
        
        // Validate required fields
        if (!access_token) {
          console.error('EASEBUZZ access token validation failed:', {
            access_token,
            allFields: response.data.data,
            availableKeys: Object.keys(response.data.data || {})
          });
          setPaymentError('EASEBUZZ access token is missing from server response');
          setIsPaymentLoading(false);
          return;
        }
        if (!merchant_key) {
          console.error('EASEBUZZ merchant key validation failed:', {
            merchant_key,
            allFields: response.data.data,
            availableKeys: Object.keys(response.data.data || {})
          });
          setPaymentError('EASEBUZZ merchant key is missing from server response');
          setIsPaymentLoading(false);
          return;
        }
        if (!order_id && !txnid) {
          setPaymentError('Order ID or Transaction ID is missing from server response');
          setIsPaymentLoading(false);
          return;
        }
        if (!amount) {
          setPaymentError('Amount is missing from server response');
          setIsPaymentLoading(false);
          return;
        }

        // Now wait for EASEBUZZ script to load before initializing checkout
        const scriptLoaded = await easebuzzScriptPromise;
        if (!scriptLoaded) {
          setPaymentError('EASEBUZZ script failed to load. Please refresh the page.');
          setIsPaymentLoading(false);
          return;
        }
        
        // Double-check that EASEBUZZ is available
        if (!window.EasebuzzCheckout) {
          // Try waiting a bit more for the script to initialize
          await new Promise(resolve => setTimeout(resolve, 500));
          if (!window.EasebuzzCheckout) {
            setPaymentError('EASEBUZZ checkout library not loaded. Please refresh the page.');
            setIsPaymentLoading(false);
            return;
          }
        }

        // Initialize EASEBUZZ checkout using the correct method as per EASEBUZZ documentation
        // Create EasebuzzCheckout instance with merchant key and environment
        // Environment can be "test" or "production" - default to "test" for development
        const environment = response.data.data.environment || 'test'; // Default to test as per HTML example
        
        console.log('Initializing EASEBUZZ with:', {
          merchant_key,
          environment,
          access_token: access_token.substring(0, 20) + '...',
          hasEasebuzzCheckout: typeof window.EasebuzzCheckout !== 'undefined'
        });
        
        try {
          // Create EASEBUZZ checkout instance (matching HTML example: new EasebuzzCheckout(key, "test"))
          const easebuzzCheckout = new window.EasebuzzCheckout(merchant_key, environment);
          
          // Prepare payment options matching EASEBUZZ iframe API
          // As per HTML example: access_key is the access_token from API
          const easebuzzOptions = {
            access_key: access_token, // Use access_key (not access_token) as per EASEBUZZ API
            onResponse: (response_data) => {
              // Handle payment response
              console.log('EASEBUZZ payment response:', response_data);
              handleEasebuzzPaymentCallback(response_data);
            },
            theme: '#3399cc' // Theme color for payment page
          };
          
          console.log('Calling EASEBUZZ initiatePayment with options:', {
            access_key: easebuzzOptions.access_key.substring(0, 20) + '...',
            hasOnResponse: typeof easebuzzOptions.onResponse === 'function',
            theme: easebuzzOptions.theme
          });
          
          // Initiate payment using initiatePayment method (matching HTML example)
          easebuzzCheckout.initiatePayment(easebuzzOptions);
          
          console.log('EASEBUZZ initiatePayment called successfully');
          // Don't set loading to false here - let the onResponse callback handle it
          // The payment modal should open now
          
        } catch (error) {
          console.error('EASEBUZZ initialization error:', error);
          console.error('Error details:', {
            message: error.message,
            stack: error.stack,
            merchant_key,
            environment,
            hasEasebuzzCheckout: typeof window.EasebuzzCheckout !== 'undefined',
            EasebuzzCheckoutType: typeof window.EasebuzzCheckout
          });
          setPaymentError(`Failed to initialize EASEBUZZ payment: ${error.message}. Please try again.`);
          setIsPaymentLoading(false);
        }
      } else {
        console.error('Payment initiation failed:', response);
        setPaymentError(response.data?.message || 'Failed to initiate payment. Please try again.');
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      setPaymentError(`Payment initiation failed: ${error.message || 'Please try again.'}`);
    } finally {
      setIsPaymentLoading(false);
    }
  };

  // Handle EASEBUZZ payment callback
  const handleEasebuzzPaymentCallback = async (paymentResponse) => {
    try {
      setIsPaymentLoading(true);
      setPaymentError('');
      setPaymentSuccess('');

      // If Easebuzz returned an error (e.g. user closed window, invalid access key), do not call verify
      if (paymentResponse && paymentResponse.error === true) {
        setPaymentError(paymentResponse.message || 'Payment was cancelled or failed.');
        setIsPaymentLoading(false);
        return;
      }

      // Backend /api/easebuzz/verify requires: txnid, status, hash
      const hasRequiredFields = paymentResponse && paymentResponse.txnid && paymentResponse.status != null;
      if (!hasRequiredFields) {
        setPaymentError(paymentResponse?.message || 'Payment response incomplete. Please try again or contact support.');
        setIsPaymentLoading(false);
        return;
      }

      // Build verification payload with API-required fields (txnid, status, hash) and context
      const verificationData = {
        txnid: paymentResponse.txnid,
        status: paymentResponse.status,
        hash: paymentResponse.hash,
        amount: formData.amount,
        fee_type: formData.fee_type,
        current_step: 2,
        // Pass through all Easebuzz response fields so backend can validate hash
        ...paymentResponse
      };

      // Verify payment via backend API
      const response = await apiService.verifyEasebuzzPayment(verificationData);
      if (response.success && response.data && response.data.success) {
        setPaymentSuccess('Payment verified successfully! Redirecting to next step...');
        setPaymentError('');

        setTimeout(() => {
          setCurrentStep(3);
          setCompletedSteps(prev => [...prev, 2]);
          setPaymentSuccess('');
        }, 4000);
      } else {
        setPaymentError(response.data?.message || 'Payment verification failed. Please contact support.');
        setPaymentSuccess('');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setPaymentError('Payment verification failed. Please contact support.');
      setPaymentSuccess('');
    } finally {
      setIsPaymentLoading(false);
    }
  };

  // Helper function to fetch districts for a specific state
  const fetchDistricts = async (stateId, districtType) => {
    if (!stateId) {
      // Clear districts if no state selected
      switch (districtType) {
        case 'organization':
          setOrganizationDistricts([]);
          break;
        case 'permanent':
          setPermanentDistricts([]);
          break;
        case 'correspondence':
          setCorrespondenceDistricts([]);
          break;
        default:
          break;
      }
      return;
    }

    try {
      // Set loading state for the specific district type
      switch (districtType) {
        case 'organization':
          setOrganizationDistrictsLoading(true);
          break;
        case 'permanent':
          setPermanentDistrictsLoading(true);
          break;
        case 'correspondence':
          setCorrespondenceDistrictsLoading(true);
          break;
        default:
          break;
      }

      const response = await apiService.getDistricts(stateId);
      
      
      if (response.success && response.data && response.data.success && response.data.data && response.data.data.districts && Array.isArray(response.data.data.districts)) {
        const districtsData = response.data.data.districts;
        
        // Set districts for the specific type
        switch (districtType) {
          case 'organization':
            setOrganizationDistricts(districtsData);
            break;
          case 'permanent':
            setPermanentDistricts(districtsData);
            break;
          case 'correspondence':
            setCorrespondenceDistricts(districtsData);
            break;
          default:
            break;
        }
      } else {
        console.error('Failed to load districts for state:', stateId);
      }
    } catch (err) {
      console.error('Error fetching districts for state:', stateId, err);
    } finally {
      // Clear loading state for the specific district type
      switch (districtType) {
        case 'organization':
          setOrganizationDistrictsLoading(false);
          break;
        case 'permanent':
          setPermanentDistrictsLoading(false);
          break;
        case 'correspondence':
          setCorrespondenceDistrictsLoading(false);
          break;
        default:
          break;
      }
    }
  };

  // Helper function to fetch post offices for a specific pincode
  const fetchPostOffices = async (pincode, postOfficeType) => {
    if (!pincode || pincode.length !== 6) {
      // Clear post offices if pincode is invalid
      switch (postOfficeType) {
        case 'permanent':
          setPermanentPostOffices([]);
          break;
        case 'correspondence':
          setCorrespondencePostOffices([]);
          break;
        default:
          break;
      }
      return;
    }

    try {
      // Set loading state for the specific post office type
      switch (postOfficeType) {
        case 'permanent':
          setPermanentPostOfficesLoading(true);
          break;
        case 'correspondence':
          setCorrespondencePostOfficesLoading(true);
          break;
        default:
          break;
      }

      const response = await apiService.getPostOfficeNames(pincode);
      
      
      if (response.success && response.data && response.data.success && response.data.data && response.data.data.offices && Array.isArray(response.data.data.offices)) {
        const postOfficesData = response.data.data.offices;
        
        // Set post offices for the specific type
        switch (postOfficeType) {
          case 'permanent':
            setPermanentPostOffices(postOfficesData);
            break;
          case 'correspondence':
            setCorrespondencePostOffices(postOfficesData);
            break;
          default:
            break;
        }
      } else {
        console.error('Failed to load post offices for pincode:', pincode);
      }
    } catch (err) {
      console.error('Error fetching post offices for pincode:', pincode, err);
    } finally {
      // Clear loading state for the specific post office type
      switch (postOfficeType) {
        case 'permanent':
          setPermanentPostOfficesLoading(false);
          break;
        case 'correspondence':
          setCorrespondencePostOfficesLoading(false);
          break;
        default:
          break;
      }
    }
  };

  // Helper function to validate step completion
  const validateStepCompletion = async () => {
    try {
      setStepValidationLoading(true);
      const response = await apiService.getStudentStep1();
      
      if (response.success && response.data.status === 'success' && response.data.student) {
        const studentData = response.data.student;
        
        // Set current step from API
        const currentStepFromAPI = studentData.current_step || 0;
        setApiCurrentStep(currentStepFromAPI);
        
        // Update completed steps based on current_step from API
        const completedStepsArray = [];
        for (let i = 1; i <= currentStepFromAPI; i++) {
          completedStepsArray.push(i);
        }
        setCompletedSteps(completedStepsArray);
        
      }
    } catch (err) {
      console.error('Error validating step completion:', err);
    } finally {
      setStepValidationLoading(false);
    }
  };

  // Helper function to submit Step 3 academic data
  const submitStep3Data = async () => {
    try {
      setIsStep3SubmissionLoading(true);
      setStep3Error('');
      
      // Get student ID for folder organization
      const studentId = authService.getStudentId();
      if (!studentId) {
        setStep3Error('Student ID not found. Please login again.');
        return;
      }
      
      // Upload files first
      const academicRecords = [];
      
      // High School (SSC) record
      if (fileUploadStatus.hs_marksheetfile.uploaded && fileUploadStatus.hs_marksheetfile.url) {
        academicRecords.push({
          examination: formData.hs_higher_school || "Matriculation",
          academic_level: "ssc",
          subject: formData.hs_qualification_subject || "All Subjects",
          year: parseInt(formData.hs_exam_year) || new Date().getFullYear(),
          board_university: formData.hs_university_board || "",
          obtained_marks: parseFloat(formData.hs_obtained_marks) || 0,
          maximum_marks: parseFloat(formData.hs_maximum_marks) || 0,
          percentage_grade: formData.hs_grade || "",
          marksheet_file: fileUploadStatus.hs_marksheetfile.url
        });
      }
      
      // Intermediate (HSC) record
      if (fileUploadStatus.int_marksheetfile.uploaded && fileUploadStatus.int_marksheetfile.url) {
        academicRecords.push({
          examination: formData.int_intermediate || "Intermediate",
          academic_level: "hsc",
          subject: formData.int_subject || "",
          year: parseInt(formData.int_exam_year) || new Date().getFullYear(),
          board_university: formData.int_university_board || "",
          obtained_marks: parseFloat(formData.int_obtained_marks) || 0,
          maximum_marks: parseFloat(formData.int_maximum_marks) || 0,
          percentage_grade: formData.int_grade || "",
          marksheet_file: fileUploadStatus.int_marksheetfile.url
        });
      }
      
      // Graduation record
      if (fileUploadStatus.gradu_marksheetfile.uploaded && fileUploadStatus.gradu_marksheetfile.url) {
        academicRecords.push({
          examination: formData.gradu_course || "Graduation",
          academic_level: "graduate",
          subject: formData.gradu_course || "",
          year: parseInt(formData.gradu_exam_year) || new Date().getFullYear(),
          board_university: formData.gradu_university_board || "",
          obtained_marks: parseFloat(formData.gradu_obtained_marks) || 0,
          maximum_marks: parseFloat(formData.gradu_maximum_marks) || 0,
          percentage_grade: formData.gradu_grade || "",
          marksheet_file: fileUploadStatus.gradu_marksheetfile.url
        });
      }
      
      // Polytechnic Diploma record
      if (fileUploadStatus.poly_marksheetfile.uploaded && fileUploadStatus.poly_marksheetfile.url) {
        academicRecords.push({
          examination: formData.poly_course || "Polytechnic Diploma",
          academic_level: "diploma",
          subject: formData.poly_course || "",
          year: parseInt(formData.poly_exam_year) || new Date().getFullYear(),
          board_university: formData.poly_university_board || "",
          obtained_marks: parseFloat(formData.poly_obtained_marks) || 0,
          maximum_marks: parseFloat(formData.poly_maximum_marks) || 0,
          percentage_grade: formData.poly_grade || "",
          marksheet_file: fileUploadStatus.poly_marksheetfile.url
        });
      }
      
      if (academicRecords.length === 0) {
        setStep3Error('Please upload at least one marksheet');
        return;
      }
      
      const academicData = {
        academic_records: academicRecords
      };
      
      
      const response = await apiService.submitAcademicRecords(academicData);
      
      if (response.success && response.data && response.data.success) {
        // After successful submission, validate step completion to update current step
        await validateStepCompletion();
        
        // Navigate to next step
        setCurrentStep(4);
        
        // Show success message
        setStep3Error('Step 3 completed successfully!');
        setTimeout(() => setStep3Error(''), 3000);
      } else {
        setStep3Error(response.data?.message || 'Failed to submit Step 3 data');
      }
    } catch (err) {
      console.error('Error submitting Step 3 data:', err);
      setStep3Error('Error submitting Step 3 data. Please try again.');
    } finally {
      setIsStep3SubmissionLoading(false);
    }
  };

  // Helper function to submit Step 4 document data
  const submitStep4Data = async () => {
    try {
      setIsStep4SubmissionLoading(true);
      setStep4Error('');
      
      // Get student ID for folder organization
      const studentId = authService.getStudentId();
      if (!studentId) {
        setStep4Error('Student ID not found. Please login again.');
        return;
      }
      
      // Check for uploaded documents and their metadata storage status
      const documents = [
        'docup_pp_photo',
        'docup_std_sign', 
        'docup_aadharcard',
        'docup_experince_emp',
        'docup_otherdoc',
        'docup_migrate'
      ];
      
      const uploadedDocuments = [];
      const documentsWithErrors = [];
      
      // Check main documents
      for (const field of documents) {
        if (fileUploadStatus[field] && fileUploadStatus[field].uploaded && fileUploadStatus[field].url) {
          if (fileUploadStatus[field].documentId) {
            uploadedDocuments.push({
              field: field,
              documentId: fileUploadStatus[field].documentId,
              status: fileUploadStatus[field].documentStatus || 'pending'
            });
          } else if (fileUploadStatus[field].metadataError) {
            documentsWithErrors.push({
              field: field,
              error: fileUploadStatus[field].metadataError
            });
          } else {
            // Document uploaded but metadata not stored yet - this shouldn't happen with immediate API calls
            documentsWithErrors.push({
              field: field,
              error: 'Document metadata not stored'
            });
          }
        }
      }
      
      // Check additional documents
      for (const docId of additionalDocuments.map(doc => doc.id)) {
        if (fileUploadStatus[docId] && fileUploadStatus[docId].uploaded && fileUploadStatus[docId].url) {
          if (fileUploadStatus[docId].documentId) {
            uploadedDocuments.push({
              field: docId,
              documentId: fileUploadStatus[docId].documentId,
              status: fileUploadStatus[docId].documentStatus || 'pending'
            });
          } else if (fileUploadStatus[docId].metadataError) {
            documentsWithErrors.push({
              field: docId,
              error: fileUploadStatus[docId].metadataError
            });
          } else {
            documentsWithErrors.push({
              field: docId,
              error: 'Document metadata not stored'
            });
          }
        }
      }
      
      if (uploadedDocuments.length === 0) {
        setStep4Error('Please upload at least one document');
        return;
      }
      
      if (documentsWithErrors.length > 0) {
        const errorMessages = documentsWithErrors.map(d => `${d.field}: ${d.error}`).join(', ');
        setStep4Error(`Some documents have errors: ${errorMessages}`);
        return;
      }
      
      // All documents are uploaded and metadata is stored
      
      // Show success message
      setStep4Error(`Step 4 completed successfully! ${uploadedDocuments.length} document(s) uploaded and stored.`);
      setTimeout(() => setStep4Error(''), 3000);
      
    } catch (err) {
      setStep4Error('Error submitting Step 4 data. Please try again.');
    } finally {
      setIsStep4SubmissionLoading(false);
    }
  };

  // Helper function to add additional document
  const addAdditionalDocument = () => {
    const newDocId = `additional_doc_${Date.now()}`;
    const newDocument = {
      id: newDocId,
      name: '',
      required: false,
      status: 'not_uploaded',
      comment: '',
      approvedBy: '',
      approvedAt: ''
    };
    
    setAdditionalDocuments(prev => [...prev, newDocument]);
    
    // Add to uploadedFileNames and fileUploadStatus
    setUploadedFileNames(prev => ({
      ...prev,
      [newDocId]: ''
    }));
    
    setFileUploadStatus(prev => ({
      ...prev,
      [newDocId]: { uploading: false, uploaded: false, url: '', error: '' }
    }));
  };

  // Helper function to remove additional document
  const removeAdditionalDocument = (docId) => {
    setAdditionalDocuments(prev => prev.filter(doc => doc.id !== docId));
    
    // Remove from uploadedFileNames and fileUploadStatus
    setUploadedFileNames(prev => {
      const newState = { ...prev };
      delete newState[docId];
      return newState;
    });
    
    setFileUploadStatus(prev => {
      const newState = { ...prev };
      delete newState[docId];
      return newState;
    });
  };

  // Helper function to update document name
  const updateDocumentName = (docId, name) => {
    setAdditionalDocuments(prev => 
      prev.map(doc => 
        doc.id === docId ? { ...doc, name } : doc
      )
    );
  };

  // Helper function to submit Step 1 data
  const submitStep1Data = async () => {
    try {
      setStep1SubmissionLoading(true);
      setError('');
      setStep1Error('');
      
      // Validate form before submission
      if (!validateStep1Form()) {
        setStep1Error('Please fix the validation errors before submitting.');
        return;
      }
      
      
      // Prepare Step 1 data for submission in new API format
      const step1Data = {
        student_name: formData.full_name,
        father_name: formData.father_name,
        mother_name: formData.mother_name,
        dob: formData.dob,
        category: formData.category,
        gender: formData.gender,
        email: formData.email,
        alternate_email: formData.alternate_email,
        alternate_mobile: formData.alternate_phone,
        mobile: formData.phone,
        aadhar_number: formData.aadhar_number,
        
        // Communication address (correspondence address)
        // If "Same as Permanent" is checked, use permanent address for communication
        communication_address: formData.is_same_as_correspondence ? {
          address: formData.permanent_address,
          pincode: formData.permanent_pincode,
          post_office: formData.permanent_city, // Use ID directly
          district: formData.permanent_district, // Use ID directly
          state: formData.permanent_state // Use ID directly
        } : {
          address: formData.correspondence_address,
          pincode: formData.correspondence_pincode,
          post_office: formData.correspondence_city, // Use ID directly
          district: formData.correspondence_district, // Use ID directly
          state: formData.correspondence_state // Use ID directly
        },
        
        // Permanent address
        permanent_address: {
          address: formData.permanent_address,
          pincode: formData.permanent_pincode,
          post_office: formData.permanent_city, // Use ID directly
          district: formData.permanent_district, // Use ID directly
          state: formData.permanent_state // Use ID directly
        },
        
        // Employment details - always send, even if empty
        employment_detail: {
          employment_status: formData.employment_status || null,
          organization_name: formData.name_of_organization || null,
          designation: formData.designation || null,
          working_since: formData.working_from || null,
          state: formData.organization_state, // Use ID directly
          district: formData.organization_district, // Use ID directly
          linkedin_profile: formData.linkedin_profile || null
        }
      };
      
      
      const response = await apiService.submitStep1(step1Data);
      
      if (response.success && response.data.status === 'success') {
        
        // After successful submission, validate step completion to update current step
        await validateStepCompletion();
        
        // Navigate to next step
        setCurrentStep(2);
        
        // Show success message
        setStep1Error('Step 1 completed successfully!');
        setTimeout(() => setStep1Error(''), 3000);
      } else {
        setStep1Error(response.data.message || 'Failed to submit Step 1 data');
      }
    } catch (err) {
      console.error('Error submitting Step 1 data:', err);
      setStep1Error('Error submitting Step 1 data. Please try again.');
    } finally {
      setStep1SubmissionLoading(false);
    }
  };

  // Helper function to handle date changes
  const handleDateChange = (date, fieldName) => {
    if (date) {
      // Convert date to YYYY-MM-DD format for form data
      const formattedDate = date.toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        [fieldName]: formattedDate
      }));
      
      // If it's the date of birth field, also calculate and set the age
      if (fieldName === 'dob') {
        const age = calculateAge(formattedDate);
        setFormData(prev => ({
          ...prev,
          age: age
        }));
      }
    } else {
      // Clear the field if date is null
      setFormData(prev => ({
        ...prev,
        [fieldName]: ''
      }));
      
      // If it's the date of birth field, also clear the age
      if (fieldName === 'dob') {
        setFormData(prev => ({
          ...prev,
          age: ''
        }));
      }
    }
  };

  // Focus management for step navigation
  useEffect(() => {
    // Small delay to ensure the DOM is updated
    const timer = setTimeout(() => {
      let firstFieldId = '';
      
      switch (currentStep) {
        case 2:
          firstFieldId = 'admission_session';
          break;
        case 3:
          firstFieldId = 'hs-higher-school';
          break;
        case 4:
          firstFieldId = 'docup-docup_pp_photo';
          break;
        default:
          return;
      }
      
      if (firstFieldId) {
        const firstField = document.getElementById(firstFieldId);
        if (firstField) {
          firstField.focus();
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [currentStep]);

  // Helper function to convert string date to Date object
  const stringToDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  // Helper function to calculate age from date of birth
  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    
    const today = new Date();
    const birth = new Date(birthDate);
    
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age.toString();
  };

  // Function to fetch existing documents from API
  const fetchExistingDocuments = async () => {
    try {      
      const response = await apiService.getDocuments();
      
      if (response.success && response.data.success) {
        const documents = response.data.data.documents;
        // Map API response to form state
        const documentTypeMapping = {
          'docup_pp_photo': ['Passport Photo', 'Student Passport Photo'],
          'docup_std_sign': ['Signature', 'Student Signature'],
          'docup_aadharcard': ['Aadhar Card', 'Aadhar Card Copy'],
          'docup_experince_emp': ['Experience Certificate', 'NOC'],
          'docup_otherdoc': ['Other Document'],
          'docup_migrate': ['Migration Certificate']
        };
        
        // Process each document from API
        documents.forEach(doc => {
          // Find matching field name based on document type
          let fieldName = null;
          for (const [field, types] of Object.entries(documentTypeMapping)) {
            if (types.some(type => doc.document_type.includes(type) || doc.document_name.includes(type))) {
              fieldName = field;
              break;
            }
          }
          
          // If no exact match found, check if it's an additional document
          if (!fieldName && doc.document_type === 'additional_document') {
            // Find or create additional document entry
            const existingAdditionalDoc = additionalDocuments.find(ad => ad.name === doc.document_name);
            if (existingAdditionalDoc) {
              fieldName = existingAdditionalDoc.id;
            } else {
              // Create new additional document entry
              const newDocId = `additional_doc_${doc.id}`;
              const newDocument = {
                id: newDocId,
                name: doc.document_name,
                required: false,
                status: doc.status,
                comment: doc.rejection_reason || '',
                approvedBy: '',
                approvedAt: ''
              };
              
              setAdditionalDocuments(prev => [...prev, newDocument]);
              fieldName = newDocId;
            }
          }
          
          if (fieldName) {
            // Extract filename from file path
            const fileName = doc.file_path.split('/').pop() || 'Document';
            
            // Update uploaded file names
            setUploadedFileNames(prev => ({
              ...prev,
              [fieldName]: fileName
            }));
            
            // Update file upload status
            setFileUploadStatus(prev => ({
              ...prev,
              [fieldName]: {
                uploading: false,
                uploaded: true,
                url: doc.file_path,
                error: '',
                fileName: fileName,
                originalName: fileName,
                documentId: doc.id,
                documentStatus: doc.status,
                metadataError: null,
                isUpdate: false,
                rejectionReason: doc.rejection_reason
              }
            }));
            
            // Update form data with file URL
            setFormData(prev => ({
              ...prev,
              [fieldName]: doc.file_path
            }));
          }
        });
        
      } else {
        console.error('Failed to fetch documents:', response.data);
      }
    } catch (error) {
      console.error('Error fetching existing documents:', error);
    }
  };

  // Function to store document metadata via API immediately after file upload
  const storeDocumentMetadata = async (fieldName, fileUrl, originalFileName) => {
    try {
      // Document type mapping for API
      const documentTypeMapping = {
        'docup_pp_photo': 'docup_pp_photo',
        'docup_std_sign': 'docup_std_sign', 
        'docup_aadharcard': 'docup_aadharcard',
        'docup_experince_emp': 'docup_experince_emp',
        'docup_otherdoc': 'docup_otherdoc',
        'docup_migrate': 'docup_migrate'
      };
      
      // Document name mapping for display
      const documentNameMapping = {
        'docup_pp_photo': 'Student Passport Photo',
        'docup_std_sign': 'Student Signature',
        'docup_aadharcard': 'Aadhar Card',
        'docup_experince_emp': 'Experience Certificate',
        'docup_otherdoc': 'Other Document',
        'docup_migrate': 'Migration Certificate'
      };

      // Determine document type and name
      let documentType, documentName;
      
      if (fieldName.startsWith('additional_doc_')) {
        // Handle additional documents
        const docName = additionalDocuments.find(doc => doc.id === fieldName)?.name || 'Additional Document';
        documentType = 'additional_document';
        documentName = docName;
      } else if (documentTypeMapping[fieldName]) {
        // Handle main documents
        documentType = documentTypeMapping[fieldName];
        documentName = documentNameMapping[fieldName];
      } else {
        // Skip non-document uploads (like academic records)
        return;
      }

      // Check if this is an update to an existing document
      const existingDocumentId = fileUploadStatus[fieldName]?.documentId;
      
      if (existingDocumentId) {
        // This is an update to an existing document
        const updateData = {
          file_path: fileUrl,
          document_type: documentType,
          document_name: documentName
        };


        // Call the update documents API
        const response = await apiService.updateDocument(existingDocumentId, updateData);
        
        if (response.success && response.data.success) {
          
          // Update file upload status with new data from API response
          setFileUploadStatus(prev => ({
            ...prev,
            [fieldName]: {
              ...prev[fieldName],
              documentId: response.data.data.id,
              documentStatus: response.data.data.status,
              metadataError: null, // Clear any previous errors
              isUpdate: true // Mark as an update
            }
          }));
        } else {
          console.error('Failed to update document metadata:', response.data);
          // Update file upload status with error
          setFileUploadStatus(prev => ({
            ...prev,
            [fieldName]: {
              ...prev[fieldName],
              metadataError: response.data.message || 'Failed to update document metadata'
            }
          }));
        }
      } else {
        // This is a new document
        const documentData = {
          file_url: fileUrl,
          document_type: documentType,
          document_name: documentName,
          sort_order: 1 // Default sort order, can be improved later
        };


        // Call the documents API
        const response = await apiService.submitDocuments(documentData);
        
        if (response.success && response.data.success) {
          
          // Update file upload status with document ID from API response
          setFileUploadStatus(prev => ({
            ...prev,
            [fieldName]: {
              ...prev[fieldName],
              documentId: response.data.data.id,
              documentStatus: response.data.data.status,
              metadataError: null, // Clear any previous errors
              isUpdate: false // Mark as new document
            }
          }));
        } else {
          console.error('Failed to store document metadata:', response.data);
          // Update file upload status with error
          setFileUploadStatus(prev => ({
            ...prev,
            [fieldName]: {
              ...prev[fieldName],
              metadataError: response.data.message || 'Failed to store document metadata'
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error storing/updating document metadata:', error);
      // Update file upload status with error
      setFileUploadStatus(prev => ({
        ...prev,
        [fieldName]: {
          ...prev[fieldName],
          metadataError: error.message
        }
      }));
    }
  };

  // Function to handle immediate file upload to Cloudflare
  const handleImmediateFileUpload = async (file, fieldName) => {
    try {
      // Check if document is verified and prevent updates
      if (fileUploadStatus[fieldName]?.documentStatus === 'verified') {
        setFileUploadStatus(prev => ({
          ...prev,
          [fieldName]: { 
            ...prev[fieldName], 
            error: 'This document is verified and cannot be updated.' 
          }
        }));
        return;
      }

      // Check authentication status first
      if (!authService.isAuthenticated()) {
        setFileUploadStatus(prev => ({
          ...prev,
          [fieldName]: { uploading: false, uploaded: false, url: '', error: 'Authentication required. Please login again.' }
        }));
        return;
      }

      // Get student ID for folder organization
      const studentId = authService.getStudentId();
      if (!studentId) {
        setFileUploadStatus(prev => ({
          ...prev,
          [fieldName]: { uploading: false, uploaded: false, url: '', error: 'Student ID not found. Please login again.' }
        }));
        return;
      }

      // Set uploading status
      setFileUploadStatus(prev => ({
        ...prev,
        [fieldName]: { uploading: true, uploaded: false, url: '', error: '' }
      }));

      // File will be uploaded with backend-generated filename

      // Determine category based on field name
      let category = 'document';
      switch (fieldName) {
        case 'hs_marksheetfile':
        case 'int_marksheetfile':
        case 'gradu_marksheetfile':
        case 'poly_marksheetfile':
          category = 'document';
          break;
        case 'docup_pp_photo':
        case 'docup_std_sign':
        case 'docup_aadharcard':
        case 'docup_experince_emp':
        case 'docup_otherdoc':
        case 'docup_migrate':
          category = 'document';
          break;
        default:
          // Additional documents and any other Step 4 uploads use document category
          if (fieldName.startsWith('additional_doc_')) {
            category = 'document';
          } else {
            category = 'document';
          }
      }


      // Upload file via backend API
      const uploadResult = await fileUploadService.uploadFile(file, category, studentId);
      
      if (uploadResult.success) {
        // Set success status
        setFileUploadStatus(prev => ({
          ...prev,
          [fieldName]: { 
            uploading: false, 
            uploaded: true, 
            url: uploadResult.url, 
            error: '',
            fileName: uploadResult.fileName,
            originalName: uploadResult.originalName
          }
        }));
        
        // Update form data with the uploaded file URL
        setFormData(prev => ({
          ...prev,
          [fieldName]: uploadResult.url // Store the URL instead of the file object
        }));

        // Immediately call the documents API to store the document metadata
        await storeDocumentMetadata(fieldName, uploadResult.url, file.name);
      } else {
        // Set error status
        setFileUploadStatus(prev => ({
          ...prev,
          [fieldName]: { uploading: false, uploaded: false, url: '', error: uploadResult.error }
        }));
      }
    } catch (error) {
      console.error(`Error uploading file ${fieldName}:`, error);
      setFileUploadStatus(prev => ({
        ...prev,
        [fieldName]: { uploading: false, uploaded: false, url: '', error: error.message }
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // If "Same as Permanent" is checked and user is changing permanent address fields,
      // also update correspondence address fields
      if (newFormData.is_same_as_correspondence && (name === 'permanent_address' || name === 'permanent_pincode' || 
          name === 'permanent_city' || name === 'permanent_district' || name === 'permanent_state')) {
        newFormData[`correspondence_${name.replace('permanent_', '')}`] = value;
      }
      
      return newFormData;
    });

    // Real-time validation for the changed field - use updated form data
    const updatedFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };
    
    // Handle district clearing when state changes
    if (name === 'permanent_state') {
      updatedFormData.permanent_district = '';
    } else if (name === 'correspondence_state') {
      updatedFormData.correspondence_district = '';
    } else if (name === 'organization_state') {
      updatedFormData.organization_district = '';
    }
    
    // If "Same as Permanent" is checked and user is changing permanent address fields,
    // also update correspondence address fields in the validation data
    if (updatedFormData.is_same_as_correspondence && (name === 'permanent_address' || name === 'permanent_pincode' || 
        name === 'permanent_city' || name === 'permanent_district' || name === 'permanent_state')) {
      updatedFormData[`correspondence_${name.replace('permanent_', '')}`] = value;
    }
    
    const fieldErrors = validateField(name, type === 'checkbox' ? checked : value, updatedFormData, { permanentDistricts, correspondenceDistricts, organizationDistricts }, { permanentPostOffices, correspondencePostOffices });
    setValidationErrors(prev => ({
      ...prev,
      [name]: fieldErrors[name] || null
    }));
    
    // Validate entire form after field change
    // Use longer timeout for checkbox and state changes to ensure state updates are complete
    const timeoutDuration = (name === 'is_same_as_correspondence' || name === 'permanent_state' || name === 'correspondence_state' || name === 'organization_state') ? 200 : 100;
    setTimeout(() => {
      // Use the updated form data for validation to ensure we have the latest state
      validateStep1Form(updatedFormData);
    }, timeoutDuration);

    // Handle state changes to fetch districts
    if (name === 'organization_state') {
      // Find state ID from states array
      const selectedState = states.find(state => state.state_name === value);
      const stateId = selectedState ? selectedState.id : null;
      fetchDistricts(stateId, 'organization');
      // Clear district selection when state changes
      setFormData(prev => ({
        ...prev,
        organization_district: ''
      }));
    } else if (name === 'permanent_state') {
      // Find state ID from states array
      const selectedState = states.find(state => state.state_name === value);
      const stateId = selectedState ? selectedState.id : null;
      fetchDistricts(stateId, 'permanent');
      // Clear district selection when state changes
      setFormData(prev => ({
        ...prev,
        permanent_district: ''
      }));
      
      // If "Same as Permanent" is checked, also fetch districts for correspondence
      if (formData.is_same_as_correspondence && stateId) {
        fetchDistricts(stateId, 'correspondence');
      }
    } else if (name === 'correspondence_state') {
      // Find state ID from states array
      const selectedState = states.find(state => state.state_name === value);
      const stateId = selectedState ? selectedState.id : null;
      fetchDistricts(stateId, 'correspondence');
      // Clear district selection when state changes
      setFormData(prev => ({
        ...prev,
        correspondence_district: ''
      }));
    } else if (name === 'is_same_as_correspondence') {
      // Handle "Same as Permanent" checkbox
      if (checked) {
        // Copy permanent address to correspondence address
        setFormData(prev => ({
          ...prev,
          correspondence_address: prev.permanent_address,
          correspondence_pincode: prev.permanent_pincode,
          correspondence_city: prev.permanent_city,
          correspondence_district: prev.permanent_district,
          correspondence_state: prev.permanent_state
        }));
        
        // Also fetch districts for correspondence if permanent state is selected
        if (formData.permanent_state) {
          const selectedState = states.find(state => state.state_name === formData.permanent_state);
          const stateId = selectedState ? selectedState.id : null;
          if (stateId) {
            fetchDistricts(stateId, 'correspondence');
          }
        }
        
        // Also copy post offices for correspondence if permanent pincode is selected
        if (formData.permanent_pincode && formData.permanent_pincode.length === 6) {
          setCorrespondencePostOffices(permanentPostOffices);
        }
        
        // Trigger validation after state update with updated form data
        setTimeout(() => {
          const updatedFormDataForCheckbox = {
            ...formData,
            is_same_as_correspondence: true,
            correspondence_address: formData.permanent_address,
            correspondence_pincode: formData.permanent_pincode,
            correspondence_city: formData.permanent_city,
            correspondence_district: formData.permanent_district,
            correspondence_state: formData.permanent_state
          };
          validateStep1Form(updatedFormDataForCheckbox);
        }, 200);
      } else {
        // Clear correspondence address when unchecked
        setFormData(prev => ({
          ...prev,
          correspondence_address: '',
          correspondence_pincode: '',
          correspondence_city: '',
          correspondence_district: '',
          correspondence_state: ''
        }));
        // Clear correspondence districts and post offices
        setCorrespondenceDistricts([]);
        setCorrespondencePostOffices([]);
        
        // Trigger validation after state update with updated form data
        setTimeout(() => {
          const updatedFormDataForUncheck = {
            ...formData,
            is_same_as_correspondence: false,
            correspondence_address: '',
            correspondence_pincode: '',
            correspondence_city: '',
            correspondence_district: '',
            correspondence_state: ''
          };
          validateStep1Form(updatedFormDataForUncheck);
        }, 200);
      }
    }
    
    // Handle pincode changes to fetch post offices
    if (name === 'permanent_pincode') {
      if (value.length === 6) {
        fetchPostOffices(value, 'permanent');
      } else {
        // Clear post offices if pincode is not 6 digits
        setPermanentPostOffices([]);
        setFormData(prev => ({
          ...prev,
          permanent_city: ''
        }));
      }
      
      // If "Same as Permanent" is checked, also fetch post offices for correspondence
      if (formData.is_same_as_correspondence && value.length === 6) {
        fetchPostOffices(value, 'correspondence');
      }
    } else if (name === 'correspondence_pincode') {
      if (value.length === 6) {
        fetchPostOffices(value, 'correspondence');
      } else {
        // Clear post offices if pincode is not 6 digits
        setCorrespondencePostOffices([]);
        setFormData(prev => ({
          ...prev,
          correspondence_city: ''
        }));
      }
    } else if (name === 'course') {
      // Handle course selection - fetch specializations and payment structures
      fetchCourseSpecializations(value);
      fetchPaymentStructures(value);
      // Clear specialization and payment type selection when course changes
      setFormData(prev => ({
        ...prev,
        sub_course: '',
        fee_type: ''
      }));
    }
    
    // Handle file uploads - track file names and trigger immediate upload
    if (type === 'file' && e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Update file name display
      setUploadedFileNames(prev => ({
        ...prev,
        [name]: file.name
      }));
      
      // Trigger immediate upload to Cloudflare
      handleImmediateFileUpload(file, name);
    }
  };

  // Validate Step 2 form
  const validateStep2Form = useCallback(() => {
    const requiredFields = ['admission_session', 'admission_type', 'course', 'sub_course', 'semester', 'fee_type', 'amount'];
    const missingFields = requiredFields.filter(field => {
      const value = formData[field];
      // Handle different value types
      if (value === null || value === undefined) {
        return true; // Field is missing
      }
      // For numeric ID fields (admission_session, course, sub_course, fee_type), check if they're valid numbers > 0
      if (field === 'admission_session' || field === 'course' || field === 'sub_course' || field === 'fee_type') {
        const numValue = parseFloat(value);
        return isNaN(numValue) || numValue <= 0;
      }
      // For amount field, check if it's a valid number > 0
      if (field === 'amount') {
        const numValue = parseFloat(value);
        return isNaN(numValue) || numValue <= 0;
      }
      // For string fields (admission_type, semester), check if they're not empty after trimming
      const stringValue = value.toString().trim();
      return stringValue === '';
    });
    
    // Debug: log missing fields if validation fails
    if (missingFields.length > 0) {
      console.log('Step 2 validation failed. Missing fields:', missingFields);
      console.log('Form data:', {
        admission_session: formData.admission_session,
        admission_type: formData.admission_type,
        course: formData.course,
        sub_course: formData.sub_course,
        semester: formData.semester,
        fee_type: formData.fee_type,
        amount: formData.amount
      });
    }
    
    return missingFields.length === 0;
  }, [formData]);

  const nextStep = async () => {
    
    if (currentStep < 4) {
      // If current step is 1 and current_step from API is 0, submit Step 1 data
      if (currentStep === 1 && !completedSteps.includes(1) && apiCurrentStep === 0) {
        await submitStep1Data();
        return;
      }
      
      // If current step is 2 and apiCurrentStep is 1, validate and initiate EASEBUZZ payment
      if (currentStep === 2 && apiCurrentStep === 1) {
        // Validate Step 2 form before proceeding
        if (!validateStep2Form()) {
          alert('Please fill all required fields in Step 2 before proceeding with payment.');
          return;
        }
        
        // Find the selected payment structure to get both ID and name
        const selectedPayment = paymentStructures.find(payment => payment.id.toString() === formData.fee_type);
        const feeTypeName = selectedPayment ? selectedPayment.fee_type : formData.fee_type;
        
        const paymentData = {
          amount: parseFloat(formData.amount),
          currency: appConfig.payment.currency,
          description: `${feeTypeName} - payment`,
          session: Number(formData.admission_session), // ensure integer id
          admission_type: formData.admission_type,
          course_id: formData.course,
          specialization_id: formData.sub_course,
          semester: formData.semester,
          fee_type: formData.fee_type, // Payment structure ID
          fee_type_name: feeTypeName, // Payment structure name
          name: formData.full_name || '',
          email: formData.email || '',
          phone: formData.phone || '',
          surl: window.location.origin + '/payment-success', // Success URL - redirect after successful payment
          furl: window.location.origin + '/payment-failure', // Failure URL - redirect after failed payment
        };
        await initiateEasebuzzPayment(paymentData);
        return;
      }
      
      // If current step is 3 and apiCurrentStep is 2, submit Step 3 academic data
      if (currentStep === 3 && apiCurrentStep === 2) {
        await submitStep3Data();
        return;
      }
      
      // If current step is 4, submit Step 4 document data
      if (currentStep === 4) {
        await submitStep4Data();
        return;
      }
      
      // Allow navigation to next step
      // Step completion validation is handled during form submission, not navigation
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    
    switch (currentStep) {
      case 1:
        return <Step1Form 
          formData={formData} 
          handleInputChange={handleInputChange} 
          states={states} 
          statesLoading={statesLoading} 
          statesError={statesError}
          organizationDistricts={organizationDistricts}
          organizationDistrictsLoading={organizationDistrictsLoading}
          permanentDistricts={permanentDistricts}
          permanentDistrictsLoading={permanentDistrictsLoading}
          correspondenceDistricts={correspondenceDistricts}
          correspondenceDistrictsLoading={correspondenceDistrictsLoading}
          permanentPostOffices={permanentPostOffices}
          permanentPostOfficesLoading={permanentPostOfficesLoading}
          correspondencePostOffices={correspondencePostOffices}
          correspondencePostOfficesLoading={correspondencePostOfficesLoading}
          validationErrors={validationErrors}
          dobDate={dobDate}
          setDobDate={setDobDate}
          handleDateChange={handleDateChange}
          step1Error={step1Error}
          apiCurrentStep={apiCurrentStep}
        />;
      case 2:
        return <Step2Form 
          formData={formData} 
          handleInputChange={handleInputChange}
          admissionSessions={admissionSessions}
          admissionSessionsLoading={admissionSessionsLoading}
          admissionSessionsError={admissionSessionsError}
          universityCourses={universityCourses}
          universityCoursesLoading={universityCoursesLoading}
          universityCoursesError={universityCoursesError}
          specializations={specializations}
          specializationsLoading={specializationsLoading}
          specializationsError={specializationsError}
          paymentStructures={paymentStructures}
          paymentStructuresLoading={paymentStructuresLoading}
          paymentStructuresError={paymentStructuresError}
          isPaymentLoading={isPaymentLoading}
          paymentError={paymentError}
          paymentSuccess={paymentSuccess}
        />;
      case 3:
        return <Step3Form 
          formData={formData} 
          handleInputChange={handleInputChange}
          hsExamYearDate={hsExamYearDate}
          setHsExamYearDate={setHsExamYearDate}
          intExamYearDate={intExamYearDate}
          setIntExamYearDate={setIntExamYearDate}
          graduExamYearDate={graduExamYearDate}
          setGraduExamYearDate={setGraduExamYearDate}
          polyExamYearDate={polyExamYearDate}
          setPolyExamYearDate={setPolyExamYearDate}
          handleDateChange={handleDateChange}
          step3Error={step3Error}
          uploadedFileNames={uploadedFileNames}
          fileUploadStatus={fileUploadStatus}
          academicRecords={academicRecords}
          academicRecordsLoading={academicRecordsLoading}
          academicRecordsError={academicRecordsError}
        />;
      case 4:
        return <Step4Form 
          formData={formData} 
          handleInputChange={handleInputChange} 
          step4Error={step4Error} 
          uploadedFileNames={uploadedFileNames} 
          fileUploadStatus={fileUploadStatus}
          additionalDocuments={additionalDocuments}
          addAdditionalDocument={addAdditionalDocument}
          removeAdditionalDocument={removeAdditionalDocument}
          updateDocumentName={updateDocumentName}
        />;
      default:
        return <Step1Form 
          formData={formData} 
          handleInputChange={handleInputChange} 
          states={states} 
          statesLoading={statesLoading} 
          statesError={statesError}
          organizationDistricts={organizationDistricts}
          organizationDistrictsLoading={organizationDistrictsLoading}
          permanentDistricts={permanentDistricts}
          permanentDistrictsLoading={permanentDistrictsLoading}
          correspondenceDistricts={correspondenceDistricts}
          correspondenceDistrictsLoading={correspondenceDistrictsLoading}
          permanentPostOffices={permanentPostOffices}
          permanentPostOfficesLoading={permanentPostOfficesLoading}
          correspondencePostOffices={correspondencePostOffices}
          correspondencePostOfficesLoading={correspondencePostOfficesLoading}
          validationErrors={validationErrors}
          dobDate={dobDate}
          setDobDate={setDobDate}
          handleDateChange={handleDateChange}
          step1Error={step1Error}
        />;
    }
  };

  return (
    <section className="applict-step-form bg-light mt-4">
      <div className="container">
        <form className="card">
          {/* Counsellor Contact Note - Always visible */}
          <div className="card-body">
            <div className="alert alert-info border-0 mb-0">
              <div className="d-flex align-items-center">
                <i className="bi bi-info-circle-fill me-3 fs-4 text-info"></i>
                <div>
                  <h6 className="mb-1 fw-bold text-info">Important Notice</h6>
                  <p className="mb-0 small">
                    Once any step is completed, it becomes non-editable. For any updates or modifications to completed steps, 
                    please connect with our counsellor. You can reach our counsellor through the contact information provided 
                    on our website or by calling our helpline.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Loading and Error Display */}
          {loading && (
            <div className="card-body text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading student data...</p>
            </div>
          )}
          
          {error && (
            <div className="card-body">
              <div className="alert alert-danger" role="alert">
                <strong>Error:</strong> {error}
              </div>
            </div>
          )}
          
          {!loading && !error && (
            <>
              <div className="card-header">
                <nav className="nav nav-pills nav-fill">
                  <a className={`nav-link tab-pills d-flex ${currentStep === 1 ? 'active' : ''}`} href="#step1">
                    <span className="d-none d-md-block">Step 1: </span> Basic Details
                  </a>
                  <a className={`nav-link tab-pills d-flex ${currentStep === 2 ? 'active' : ''}`} href="#step2">
                    <span className="d-none d-md-block">Step 2:</span> Course & Fee
                  </a>
                  <a className={`nav-link tab-pills d-flex ${currentStep === 3 ? 'active' : ''}`} href="#step3">
                    <span className="d-none d-md-block">Step 3:</span> Academic Details
                  </a>
                  <a className={`nav-link tab-pills d-flex ${currentStep === 4 ? 'active' : ''}`} href="#step4">
                    <span className="d-none d-md-block">Step 4:</span> Upload Documents
                  </a>
                </nav>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <p className="mt-4 px-3">
                    <b>Note</b>: Please provide your demographic information as per your Matriculation/10th Certificate.
                  </p>
                </div>
              </div>
              <div className="card-body">
                {renderStepContent()}
              </div>
              <div className="stepfooter card-footer mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  {/* Left side - Success/Error messages */}
                  <div className="flex-grow-1">
                    {currentStep === 1 && !isFormValid && apiCurrentStep === 0 && (
                      <div className="text-muted small">
                        <div className="d-flex align-items-center mb-1">
                          <i className="bi bi-exclamation-circle me-1"></i>
                          <span>Please fill all required fields to enable "Save and Continue"</span>
                        </div>
                        <div className="text-muted">
                          {Object.keys(validationErrors).length} field(s) need attention
                        </div>
                      </div>
                    )}
                    {currentStep === 1 && isFormValid && !completedSteps.includes(1) && apiCurrentStep === 0 && (
                      <div className="text-success small d-flex align-items-center">
                        <i className="bi bi-check-circle me-1"></i>
                        <span>All required fields completed. Ready to save!</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Right side - Buttons */}
                  <div className="d-flex gap-2">
                  <button 
                    type="button" 
                    id="back_button" 
                    className="back-btn btn btn-primary" 
                    onClick={prevStep}
                    disabled={currentStep === 1}
                  >
                    Back
                  </button>
                  <button 
                    type="button" 
                    id="next_button" 
                    className="btn btn-primary"
                    onClick={nextStep}
                    disabled={currentStep === 4 || stepValidationLoading || step1SubmissionLoading || isPaymentLoading || isStep3SubmissionLoading || isStep4SubmissionLoading || (currentStep === 1 && !isFormValid && apiCurrentStep === 0) || (currentStep === 2 && apiCurrentStep === 1 && (!formData.fee_type || !formData.amount))}
                  >
                      {step1SubmissionLoading ? 'Saving...' : 
                       stepValidationLoading ? 'Validating...' : 
                       isPaymentLoading ? 'Processing Payment...' :
                       isStep3SubmissionLoading ? 'Saving Academic Records...' :
                       isStep4SubmissionLoading ? 'Uploading Documents...' :
                       (currentStep === 1 && !completedSteps.includes(1) && apiCurrentStep === 0) ? 'Save and Continue' :
                       (currentStep === 2 && apiCurrentStep === 1) ? 'Pay and Continue' :
                       (currentStep === 3 && apiCurrentStep === 2) ? 'Save and Continue' :
                       (currentStep === 4 ? 'Submit' : 'Next')}
                  </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </form>
      </div>
    </section>
  );
};

// Step 1: Basic Details Form Component
const Step1Form = ({ 
  formData, 
  handleInputChange, 
  states, 
  statesLoading, 
  statesError,
  organizationDistricts,
  organizationDistrictsLoading,
  permanentDistricts,
  permanentDistrictsLoading,
  correspondenceDistricts,
  correspondenceDistrictsLoading,
  permanentPostOffices,
  permanentPostOfficesLoading,
  correspondencePostOffices,
  correspondencePostOfficesLoading,
  validationErrors,
  dobDate,
  setDobDate,
  handleDateChange,
  step1Error,
  apiCurrentStep
}) => {
  // Helper function to render validation error
  const renderValidationError = (fieldName) => {
    // Don't show validation errors if step is already completed (apiCurrentStep > 0)
    if (apiCurrentStep > 0) {
      return null;
    }
    
    if (validationErrors && validationErrors[fieldName]) {
      return <div className="text-danger small mt-1">{validationErrors[fieldName]}</div>;
    }
    return null;
  };

  // Helper function to render state options
  const renderStateOptions = () => {
    if (statesLoading) {
      return <option value="">Loading states...</option>;
    }
    
    if (statesError) {
      return <option value="">Error loading states</option>;
    }
    
    if (!states || states.length === 0) {
      return <option value="">No states available</option>;
    }
    
    return (
      <>
        <option value="">Select State</option>
        {states.map((state) => (
          <option key={state.id} value={state.id}>
            {state.state_name} ({state.state_code})
          </option>
        ))}
      </>
    );
  };

  // Helper function to render district options for organization
  const renderOrganizationDistrictOptions = () => {
    
    if (organizationDistrictsLoading) {
      return <option value="">Loading districts...</option>;
    }
    
    if (!organizationDistricts || organizationDistricts.length === 0) {
      return <option value="">Select state first</option>;
    }
    
    return (
      <>
        <option value="">Select District</option>
        {organizationDistricts.map((district) => (
          <option key={district.id} value={district.id}>
            {district.district_name}
          </option>
        ))}
      </>
    );
  };

  // Helper function to render district options for permanent address
  const renderPermanentDistrictOptions = () => {
    if (permanentDistrictsLoading) {
      return <option value="">Loading districts...</option>;
    }
    
    if (!permanentDistricts || permanentDistricts.length === 0) {
      return <option value="">Select state first</option>;
    }
    
    return (
      <>
        <option value="">Select District</option>
        {permanentDistricts.map((district) => (
          <option key={district.id} value={district.id}>
            {district.district_name}
          </option>
        ))}
      </>
    );
  };

  // Helper function to render district options for correspondence address
  const renderCorrespondenceDistrictOptions = () => {
    if (correspondenceDistrictsLoading) {
      return <option value="">Loading districts...</option>;
    }
    
    if (!correspondenceDistricts || correspondenceDistricts.length === 0) {
      return <option value="">Select state first</option>;
    }
    
    return (
      <>
        <option value="">Select District</option>
        {correspondenceDistricts.map((district) => (
          <option key={district.id} value={district.id}>
            {district.district_name}
          </option>
        ))}
      </>
    );
  };

  return (
    <div className="tab">
      <div className="applic-wrap">
        <div id="step_1_form">
          {/* Step 1 Error Display */}
          {step1Error && (
            <div className="row mb-3">
              <div className="col-12">
                <div className={`alert ${step1Error.includes('successfully') ? 'alert-success' : 'alert-danger'}`} role="alert">
                  <div className="d-flex align-items-center">
                    <i className={`bi ${step1Error.includes('successfully') ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                    <div>
                      <strong>{step1Error.includes('successfully') ? 'Success:' : 'Error:'}</strong> {step1Error}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="pt-4 row">
            {/* Name */}
            <div className="mb-3 col-md-4">
              <label htmlFor="full_name" className="fw-600 fs-13">Full Name<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="full_name" 
                name="full_name" 
                placeholder="Full Name" 
                autoComplete="off" 
                required 
                value={formData.full_name}
                onChange={handleInputChange}
              />
              {renderValidationError('full_name')}
            </div>
            {/* Father Name */}
            <div className="mb-3 col-md-4">
              <label htmlFor="father_name" className="fw-600 fs-13">Father Name<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="father_name" 
                name="father_name" 
                placeholder="Father Name" 
                autoComplete="off" 
                required 
                value={formData.father_name}
                onChange={handleInputChange}
              />
              {renderValidationError('father_name')}
            </div>
            {/* Mother Name */}
            <div className="mb-3 col-md-4">
              <label htmlFor="mother_name" className="fw-600 fs-13">Mother Name<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="mother_name" 
                name="mother_name" 
                placeholder="Mother Name" 
                autoComplete="off" 
                required 
                value={formData.mother_name}
                onChange={handleInputChange}
              />
              {renderValidationError('mother_name')}
            </div>
            {/* DOB */}
            <div className="mb-3 col-md-3">
              <label htmlFor="dob" className="fw-600 fs-13">Date of Birth<span className="text-danger">*</span></label>
              <DatePicker
                selected={dobDate}
                onChange={(date) => {
                  setDobDate(date);
                  handleDateChange(date, 'dob');
                }}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                className="form-control"
                id="dob"
                name="dob"
                autoComplete="off"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                yearDropdownItemNumber={100}
                scrollableYearDropdown
                maxDate={new Date()}
                isClearable
                required
              />
            </div>
            <div className="mb-3 col-md-1">
              <label htmlFor="age" className="fw-600 fs-13">Age<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="age" 
                placeholder="" 
                autoComplete="off" 
                disabled 
                value={formData.age}
              />
            </div>
            {/* Gender */}
            <div className="mb-3 col-md-4">
              <label htmlFor="gender" className="fw-600 fs-13">Gender<span className="text-danger">*</span></label>
              <select 
                className="form-select" 
                id="gender" 
                name="gender" 
                required 
                value={formData.gender}
                onChange={handleInputChange}
              >
                <option value=""> Select</option>
                <option value="Male"> Male</option>
                <option value="Female"> Female</option>
                <option value="Other"> Other</option>
              </select>
              {renderValidationError('gender')}
            </div>
            {/* Category */}
            <div className="mb-3 col-md-4">
              <label htmlFor="category" className="fw-600 fs-13">Category<span className="text-danger">*</span></label>
              <select 
                className="form-select" 
                id="category" 
                name="category" 
                required 
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value=""> Select</option>
                <option value="general"> General</option>
                <option value="obc"> OBC</option>
                <option value="st"> ST</option>
                <option value="sc"> SC</option>
                <option value="other"> Other</option>
              </select>
              {renderValidationError('category')}
            </div>
            <div className="mb-3 col-md-4">
              <label htmlFor="aadhar_number" className="fw-600 fs-13">Aadhar Number<span className="text-danger">*</span></label>
              <input 
                className="form-control" 
                type="tel" 
                id="aadhar_number" 
                name="aadhar_number" 
                maxLength="14" 
                placeholder="XXXX-XXXX-XXXX" 
                autoComplete="off" 
                value={formData.aadhar_number}
                onChange={handleInputChange}
              />
              {renderValidationError('aadhar_number')}
            </div>
          </div>
          
          <div className="pt-4 row">
            <h5 className="p-2 col-12 fw-bold">Employment Details <span className="text-muted small">(Optional)</span></h5>
            {/* Employment Status */}
            <div className="mb-3 col-md-4">
              <label htmlFor="employment_status" className="fw-600 fs-13">Employment Status</label>
              <select 
                className="form-select" 
                id="employment_status" 
                name="employment_status" 
                value={formData.employment_status}
                onChange={handleInputChange}
              >
                <option value=""> -- Select --</option>
                <option value="employed">Employed</option>
                <option value="unemployed">Unemployed</option>
                <option value="student">Student</option>
                <option value="freelancer">Freelancer</option>
                <option value="government_job">Government Job</option>
                <option value="private_job">Private Job</option>
                <option value="retired">Retired</option>
                <option value="other">Other</option>
              </select>
            </div>
            {/* Name of Organization */}
            <div className="mb-3 col-md-4">
              <label htmlFor="name_of_organization" className="fw-600 fs-13">Name of Organization</label>
              <input 
                type="text" 
                className="form-control" 
                id="name_of_organization" 
                name="name_of_organization" 
                placeholder="Name of Organization" 
                autoComplete="off" 
                value={formData.name_of_organization}
                onChange={handleInputChange}
              />
              {renderValidationError('name_of_organization')}
            </div>
            {/* Designation */}
            <div className="mb-3 col-md-4">
              <label htmlFor="designation" className="fw-600 fs-13">Designation</label>
              <input 
                type="text" 
                className="form-control" 
                id="designation" 
                name="designation" 
                placeholder="Designation" 
                autoComplete="off" 
                value={formData.designation}
                onChange={handleInputChange}
              />
              {renderValidationError('designation')}
            </div>
            {/* Working From */}
            <div className="mb-3 col-md-4">
              <label htmlFor="working_from" className="fw-600 fs-13">Working Since</label>
              <select 
                className="form-control" 
                id="working_from" 
                name="working_from" 
                value={formData.working_from}
                onChange={handleInputChange}
              >
                <option value="">Select Year</option>
                {Array.from({ length: 50 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
              {renderValidationError('working_from')}
            </div>
            {/* State */}
            <div className="mb-3 col-md-4">
              <label htmlFor="organization_state" className="fw-600 fs-13">State</label>
              <select 
                className="form-control" 
                id="organization_state"
                name="organization_state"
                value={formData.organization_state}
                onChange={handleInputChange}
              >
                {renderStateOptions()}
              </select>
            </div>
            <div className="mb-3 col-md-4">
              <label htmlFor="organization_district" className="fw-600 fs-13">District</label>
              <select 
                className="form-control" 
                id="organization_district"
                name="organization_district"
                value={formData.organization_district}
                onChange={handleInputChange}
              >
                {renderOrganizationDistrictOptions()}
              </select>
            </div>
            
            {/* LinkedIn Profile */}
            <div className="mb-3 col-md-8">
              <label htmlFor="linkedin_profile" className="fw-600 fs-13">LinkedIn Profile<span className="text-danger"></span></label>
              <input 
                type="url" 
                className="form-control" 
                id="linkedin_profile" 
                name="linkedin_profile" 
                style={{textTransform: 'none'}} 
                placeholder="LinkedIn Profile" 
                autoComplete="off" 
                value={formData.linkedin_profile}
                onChange={handleInputChange}
              />
              {renderValidationError('linkedin_profile')}
              <dt style={{fontSize: '12px', color: '#6c757d', marginTop: '4px', fontWeight: '500'}}>
                Adding your LinkedIn profile helps you grow your network and find job opportunities. We will help you along the way...
              </dt>
            </div>
          </div>
          
          <div className="pt-4 row">
            {/* Communication Details */}
            <h5 className="p-2 col-12 fw-bold">Communication Details</h5>
            {/* Email */}
            <div className="mb-3 col-md-6">
              <label htmlFor="email" className="fw-600 fs-13">Email<span className="text-danger">*</span></label>
              <input 
                type="email" 
                className="form-control" 
                id="email" 
                placeholder="Email" 
                autoComplete="off" 
                readOnly 
                value={formData.email}
              />
            </div>
            {/* Alternate Email */}
            <div className="mb-3 col-md-6">
              <label htmlFor="alternate_email" className="fw-600 fs-13">Alternate Email (Optional)</label>
              <input 
                type="email" 
                className="form-control" 
                id="alternate_email" 
                name="alternate_email" 
                placeholder="Alternate Email" 
                autoComplete="off" 
                value={formData.alternate_email}
                onChange={handleInputChange}
              />
              {renderValidationError('alternate_email')}
            </div>
            {/* Contact */}
            <div className="mb-3 col-md-6">
              <label htmlFor="phone" className="fw-600 fs-13">Mobile<span className="text-danger">*</span></label>
              <input 
                type="tel" 
                maxLength="10" 
                minLength="10" 
                className="form-control" 
                id="phone" 
                name="phone"
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) e.preventDefault();
                }}
                placeholder="Phone" 
                autoComplete="off" 
                value={formData.phone}
                onChange={handleInputChange}
              />
              {renderValidationError('phone')}
            </div>
            {/* Alternate Phone */}
            <div className="mb-3 col-md-6">
              <label htmlFor="alternate_phone" className="fw-600 fs-13">Alternate Mobile<span className="text-danger"></span></label>
              <input 
                type="tel" 
                maxLength="10" 
                minLength="10" 
                className="form-control" 
                id="alternate_phone" 
                name="alternate_phone" 
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) e.preventDefault();
                }}
                placeholder="Alternate Phone" 
                autoComplete="off" 
                value={formData.alternate_phone}
                onChange={handleInputChange}
              />
            </div>
          </div>
          
          <div className="pt-4 row">
            {/* Permanent Address */}
            <div className="mb-3 col-md-6">
              <div className="row">
                <h5 className="mb-4 col-12 fw-bold">Permanent Address<span className="text-danger">*</span></h5>
                {/* Address */}
                <div className="mb-3 col-md-12">
                  <label htmlFor="permanent_address" className="fw-600 fs-13">Address<span className="text-danger">*</span></label>
                  <textarea 
                    className="form-control form-control-textarea" 
                    placeholder="Address" 
                    name="permanent_address" 
                    id="permanent_address" 
                    rows="3" 
                    autoComplete="nope"
                    value={formData.permanent_address}
                    onChange={handleInputChange}
                  ></textarea>
                  {renderValidationError('permanent_address')}
                </div>
              </div>
              <div className="row">
                {/* Pincode */}
                <div className="mb-3 col-md-6">
                  <label htmlFor="permanent_pincode" className="fw-600 fs-13">Pincode<span className="text-danger">*</span></label>
                  <input 
                    type="tel" 
                    maxLength="6" 
                    minLength="6" 
                    className="form-control" 
                    id="permanent_pincode" 
                    name="permanent_pincode" 
                    placeholder="Pincode" 
                    autoComplete="nope"
                    value={formData.permanent_pincode}
                    onChange={handleInputChange}
                  />
                  {renderValidationError('permanent_pincode')}
                </div>
                {/* City */}
                <div className="mb-3 col-md-6">
                  <label htmlFor="permanent_city" className="fw-600 fs-13">Post Office<span className="text-danger">*</span></label>
                  <select 
                    className="form-select" 
                    id="permanent_city" 
                    name="permanent_city" 
                    required 
                    value={formData.permanent_city}
                    onChange={handleInputChange}
                    disabled={permanentPostOfficesLoading}
                  >
                    <option value="">
                      {permanentPostOfficesLoading ? 'Loading...' : 'Select Post Office'}
                    </option>
                    {permanentPostOffices.map((office, index) => (
                      <option key={office.id || index} value={office.id}>
                        {office.office_name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* State */}
                <div className="mb-3 col-md-6">
                  <label htmlFor="permanent_state" className="fw-600 fs-13">State<span className="text-danger">*</span></label>
                  <select 
                    className="form-control" 
                    id="permanent_state" 
                    name="permanent_state" 
                    required 
                    value={formData.permanent_state}
                    onChange={handleInputChange}
                  >
                    {renderStateOptions()}
                  </select>
                  {renderValidationError('permanent_state')}
                </div>
                {/* District */}
                <div className="mb-3 col-md-6">
                  <label htmlFor="permanent_district" className="fw-600 fs-13">District<span className="text-danger">*</span></label>
                  <select 
                    className="form-select" 
                    id="permanent_district" 
                    name="permanent_district" 
                    required 
                    value={formData.permanent_district}
                    onChange={handleInputChange}
                  >
                    {renderPermanentDistrictOptions()}
                  </select>
                  {renderValidationError('permanent_district')}
                </div>
              </div>
            </div>
            {/* Correspondence Address */}
            <div className="mb-3 col-md-6">
              <div className="row">
                <div className="col-12 d-flex justify-content-between">
                  <div>
                    <h5 className="mb-4 fw-bold text-start">Correspondence Address<span className="text-danger">*</span></h5>
                  </div>
                  <div>
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="is_same_as_correspondence"
                      name="is_same_as_correspondence"
                      checked={formData.is_same_as_correspondence}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="is_same_as_correspondence" className="form-check-label fw-bold">Same as Permanent</label>
                  </div>
                </div>
                {/* Address */}
                <div className="mb-3 col-md-12">
                  <label htmlFor="correspondence_address" className="fw-600 fs-13">Address<span className="text-danger">*</span></label>
                  <textarea 
                    className="form-control form-control-textarea" 
                    placeholder="Address" 
                    name="correspondence_address" 
                    id="correspondence_address" 
                    rows="3" 
                    autoComplete="nope"
                    value={formData.correspondence_address}
                    onChange={handleInputChange}
                    readOnly={formData.is_same_as_correspondence}
                    style={formData.is_same_as_correspondence ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                  ></textarea>
                </div>
                {/* Pincode */}
                <div className="mb-3 col-md-6">
                  <label htmlFor="correspondence_pincode" className="fw-600 fs-13">Pincode<span className="text-danger">*</span></label>
                  <input 
                    type="tel" 
                    maxLength="6" 
                    minLength="6" 
                    className="form-control" 
                    id="correspondence_pincode" 
                    name="correspondence_pincode" 
                    placeholder="Pincode" 
                    autoComplete="nope"
                    value={formData.correspondence_pincode}
                    onChange={handleInputChange}
                    readOnly={formData.is_same_as_correspondence}
                    style={formData.is_same_as_correspondence ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                  />
                </div>
                {/* City */}
                <div className="mb-3 col-md-6">
                  <label htmlFor="correspondence_city" className="fw-600 fs-13">Post Office<span className="text-danger">*</span></label>
                  <select 
                    className="form-select" 
                    id="correspondence_city" 
                    name="correspondence_city" 
                    required 
                    value={formData.correspondence_city}
                    onChange={handleInputChange}
                    disabled={formData.is_same_as_correspondence || correspondencePostOfficesLoading}
                    style={formData.is_same_as_correspondence ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                  >
                    <option value="">
                      {correspondencePostOfficesLoading ? 'Loading...' : 'Select Post Office'}
                    </option>
                    {correspondencePostOffices.map((office, index) => (
                      <option key={office.id || index} value={office.id}>
                        {office.office_name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* State */}
                <div className="mb-3 col-md-6">
                  <label htmlFor="correspondence_state" className="fw-600 fs-13">State<span className="text-danger">*</span></label>
                  <select 
                    className="form-control" 
                    id="correspondence_state" 
                    name="correspondence_state" 
                    required 
                    value={formData.correspondence_state}
                    onChange={handleInputChange}
                    disabled={formData.is_same_as_correspondence}
                    style={formData.is_same_as_correspondence ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                  >
                    {renderStateOptions()}
                  </select>
                  {renderValidationError('correspondence_state')}
                </div>
                {/* District */}
                <div className="mb-3 col-md-6">
                  <label htmlFor="correspondence_district" className="fw-600 fs-13">District<span className="text-danger">*</span></label>
                  <select 
                    className="form-select" 
                    id="correspondence_district" 
                    name="correspondence_district" 
                    required 
                    value={formData.correspondence_district}
                    onChange={handleInputChange}
                    disabled={formData.is_same_as_correspondence}
                    style={formData.is_same_as_correspondence ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                  >
                    {renderCorrespondenceDistrictOptions()}
                  </select>
                  {renderValidationError('correspondence_district')}
                </div>
              </div>
            </div>
          </div>
          <div className="p-2"></div>
        </div>
      </div>
    </div>
  );
};

// Step 2: Course & Fee Form Component
const Step2Form = ({ formData, handleInputChange, admissionSessions, admissionSessionsLoading, admissionSessionsError, universityCourses, universityCoursesLoading, universityCoursesError, specializations, specializationsLoading, specializationsError, paymentStructures, paymentStructuresLoading, paymentStructuresError, isPaymentLoading, paymentError, paymentSuccess }) => {
  const [feeType, setFeeType] = useState(formData.fee_type || '');
  const [amount, setAmount] = useState(formData.amount || '');

  // Update local state when formData changes (for pre-filled data)
  useEffect(() => {
    setFeeType(formData.fee_type || '');
    setAmount(formData.amount || '');
  }, [formData.fee_type, formData.amount]);

  // Update amount when paymentStructures are loaded and fee_type is pre-selected
  useEffect(() => {
    if (formData.fee_type && paymentStructures.length > 0) {
      const selectedPayment = paymentStructures.find(payment => payment.id.toString() === formData.fee_type);
      if (selectedPayment) {
        setAmount(selectedPayment.formatted_amount);
      } else {
      }
    }
  }, [formData.fee_type, paymentStructures]);

  const handleFeeTypeChange = (e) => {
    const value = e.target.value;
    setFeeType(value);
    handleInputChange(e);
    
    // Find the selected payment structure and set the amount
    if (value && paymentStructures.length > 0) {
      const selectedPayment = paymentStructures.find(payment => payment.id.toString() === value);
      if (selectedPayment) {
        setAmount(selectedPayment.formatted_amount);
        // Update form data with raw amount for API calls
        handleInputChange({
          target: {
            name: 'amount',
            value: selectedPayment.amount
          }
        });
      } else {
        setAmount('');
        handleInputChange({
          target: {
            name: 'amount',
            value: ''
          }
        });
      }
    } else {
      setAmount('');
      handleInputChange({
        target: {
          name: 'amount',
          value: ''
        }
      });
    }
  };

        return (
    <div className="tab">
      <div className="applic-wrap">
        <div id="step_2_form">
          {/* Payment Success Display */}
          {paymentSuccess && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="alert alert-success" role="alert">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-check-circle me-2"></i>
                    <div>
                      <strong>Success:</strong> {paymentSuccess}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Payment Error Display */}
          {paymentError && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="alert alert-danger" role="alert">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <div>
                      <strong>Payment Error:</strong> {paymentError}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="pt-4 row">
            {/* Session */}
            <div className="mb-3 col-md-6">
              <label htmlFor="admission_session" className="fw-600 fs-13">Session<span className="text-danger">*</span></label>
              <select 
                className="form-select" 
                id="admission_session" 
                name="admission_session" 
                required 
                value={formData.admission_session}
                onChange={handleInputChange}
                disabled={admissionSessionsLoading}
              >
                <option value="">{admissionSessionsLoading ? 'Loading...' : 'Select Session'}</option>
                {admissionSessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.session}
                  </option>
                ))}
              </select>
              {admissionSessionsError && (
                <div className="text-danger mt-1 small">{admissionSessionsError}</div>
              )}
            </div>

            {/* Admission Type */}
            <div className="mb-3 col-md-6">
              <label htmlFor="admission_type" className="fw-600 fs-13">Admission Type<span className="text-danger">*</span></label>
              <select 
                className="form-select" 
                id="admission_type" 
                name="admission_type" 
                required 
                value={formData.admission_type}
                onChange={handleInputChange}
              >
                <option value="Fresh">Fresh</option>
                <option value="Lateral">Lateral</option>
              </select>
            </div>

            {/* Course */}
            <div className="mb-3 col-md-4">
              <label htmlFor="course" className="fw-600 fs-13">Course<span className="text-danger">*</span></label>
              <select 
                className="form-select" 
                id="course" 
                name="course" 
                required 
                value={formData.course}
                onChange={handleInputChange}
                disabled={universityCoursesLoading}
              >
                <option value="">{universityCoursesLoading ? 'Loading...' : 'Select Course'}</option>
                {universityCourses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </select>
              {universityCoursesError && (
                <div className="text-danger mt-1 small">{universityCoursesError}</div>
              )}
            </div>

            {/* Specialization */}
            <div className="mb-3 col-md-4">
              <label htmlFor="sub_course" className="fw-600 fs-13">Specialization<span className="text-danger">*</span></label>
              <select 
                className="form-select" 
                id="sub_course" 
                name="sub_course" 
                required 
                value={formData.sub_course}
                onChange={handleInputChange}
                disabled={specializationsLoading || !formData.course}
              >
                <option value="">
                  {!formData.course ? 'Select Course First' : 
                   specializationsLoading ? 'Loading...' : 'Select Specialization'}
                </option>
                {specializations.map((specialization) => (
                  <option key={specialization.id} value={specialization.id}>
                    {specialization.name}
                  </option>
                ))}
              </select>
              {specializationsError && (
                <div className="text-danger mt-1 small">{specializationsError}</div>
              )}
            </div>

            {/* Semester */}
            <div className="mb-3 col-md-4">
              <label htmlFor="semester" className="fw-600 fs-13">Semester<span className="text-danger">*</span></label>
              <select 
                className="form-select" 
                id="semester" 
                name="semester" 
                required 
                value={formData.semester}
                onChange={handleInputChange}
              >
                <option value="">Select Semester</option>
                <option value="1">1</option>
                <option value="3">3</option>
              </select>
            </div>
          </div>

          <div className="pt-2 row">
            <hr />
            <h5 className="mt-2 col-12 mb-4 fw-bold">Fee</h5>

            <div className="mb-3 col-md-6">
              <label htmlFor="fee_type" className="fw-600 fs-13">Payment Type<span className="text-danger">*</span></label>
              <select 
                id="fee_type" 
                name="fee_type" 
                className="form-control" 
                value={feeType}
                onChange={handleFeeTypeChange}
                disabled={paymentStructuresLoading || !formData.course}
                required
              >
                <option value="">
                  {!formData.course ? 'Select Course First' : 
                   paymentStructuresLoading ? 'Loading...' : 'Select Payment Type'}
                </option>
                {paymentStructures.map((payment) => (
                  <option key={payment.id} value={payment.id}>
                    {payment.fee_type} - {payment.formatted_amount}
                  </option>
                ))}
              </select>
              {paymentStructuresError && (
                <div className="text-danger mt-1 small">{paymentStructuresError}</div>
              )}
            </div>
            
            <div className="mb-3 col-md-6">
              <label htmlFor="amount" className="fw-600 fs-13">Total Payable<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                name="amount" 
                readOnly 
                id="amount" 
                placeholder="Fee"
                value={amount}
                required
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// Step 3: Academic Details Form Component
const Step3Form = ({ 
  formData, 
  handleInputChange,
  hsExamYearDate,
  setHsExamYearDate,
  intExamYearDate,
  setIntExamYearDate,
  graduExamYearDate,
  setGraduExamYearDate,
  polyExamYearDate,
  setPolyExamYearDate,
  handleDateChange,
  step3Error,
  uploadedFileNames,
  fileUploadStatus,
  academicRecords,
  academicRecordsLoading,
  academicRecordsError
}) => {
  return (
    <div className="tab">
      <div className="applic-wrap">
        <div id="step_3_form">
          {/* Step 3 Error Display */}
          {step3Error && (
            <div className="row mb-3">
              <div className="col-12">
                <div className={`alert ${step3Error.includes('successfully') ? 'alert-success' : 'alert-danger'}`} role="alert">
                  <div className="d-flex align-items-center">
                    <i className={`bi ${step3Error.includes('successfully') ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                    <div>
                      <strong>{step3Error.includes('successfully') ? 'Success:' : 'Error:'}</strong> {step3Error}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Academic Records Loading */}
          {academicRecordsLoading && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="alert alert-info" role="alert">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    <div>
                      <strong>Loading academic records...</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Academic Records Error */}
          {academicRecordsError && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="alert alert-warning" role="alert">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <div>
                      <strong>Warning:</strong> {academicRecordsError}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="pt-4 row">
            <h5 className="p-2 col-12 fw-bold">High School</h5>
            {/* Name */}
            <div className="mb-3 col-md-3">
              <label htmlFor="hs-higher-school" className="fw-600 fs-13">High School<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="hs-higher-school" 
                name="hs_higher_school" 
                placeholder="Higher School" 
                autoComplete="off" 
                required 
                value={formData.hs_higher_school}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="hs-qualification-subject" className="fw-600 fs-13">All Subject<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="hs-qualification-subject" 
                name="hs_qualification_subject" 
                placeholder="All Subject" 
                value={formData.hs_qualification_subject}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="hs-exam-year" className="fw-600 fs-13">Select Year<span className="text-danger">*</span></label>
              <DatePicker
                selected={hsExamYearDate}
                onChange={(date) => {
                  setHsExamYearDate(date);
                  handleDateChange(date, 'hs_exam_year');
                }}
                dateFormat="yyyy"
                placeholderText="Select Year"
                className="form-control"
                id="hs-exam-year"
                name="hs_exam_year"
                showYearDropdown
                showMonthDropdown={false}
                dropdownMode="select"
                yearDropdownItemNumber={50}
                scrollableYearDropdown
                maxDate={new Date()}
                isClearable
                required
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="hs-university-board" className="fw-600 fs-13">Board/ University<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="hs-university-board" 
                name="hs_university_board" 
                placeholder="university|board" 
                value={formData.hs_university_board}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="hs-maximum-marks" className="fw-600 fs-13">Maxmimum Marks<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="hs-maximum-marks" 
                name="hs_maximum_marks" 
                placeholder="Maximum Marks" 
                value={formData.hs_maximum_marks}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="hs-obtained-marks" className="fw-600 fs-13">Obtained Marks<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="hs-obtained-marks" 
                name="hs_obtained_marks" 
                placeholder="Obtained Marks" 
                value={formData.hs_obtained_marks}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="hs-grade" className="fw-600 fs-13">Percentage/ Grade<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="hs-grade" 
                name="hs_grade" 
                placeholder="percentage / Grade" 
                value={formData.hs_grade}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="hs-marksheetfile" className="fw-600 fs-13">Upload Marksheet<span className="text-danger">*</span></label>
              <input 
                type="file" 
                className="form-control" 
                id="hs-marksheetfile" 
                name="hs_marksheetfile" 
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
              {uploadedFileNames.hs_marksheetfile && (
                <div className="mt-1">
                  {fileUploadStatus.hs_marksheetfile.uploading && (
                    <small className="text-info">
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Uploading: {uploadedFileNames.hs_marksheetfile}...
                    </small>
                  )}
                  {fileUploadStatus.hs_marksheetfile.uploaded && (
                    <div className="mt-1">
                      <small className="text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        Uploaded: 
                        <a 
                          href={fileUploadStatus.hs_marksheetfile.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-decoration-none ms-1"
                        >
                          {uploadedFileNames.hs_marksheetfile}
                        </a>
                      </small>
                    </div>
                  )}
                  {fileUploadStatus.hs_marksheetfile.error && (
                    <small className="text-danger">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      Error: {fileUploadStatus.hs_marksheetfile.error}
                    </small>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="pt-4 row">
            <h5 className="p-2 col-12 fw-bold">Intermediate</h5>
            {/* Name */}
            <div className="mb-3 col-md-3">
              <label htmlFor="int-intermediate" className="fw-600 fs-13">12<sub>th</sub> Pass<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="int-intermediate" 
                name="int_intermediate" 
                placeholder="12th pass" 
                autoComplete="off" 
                required 
                value={formData.int_intermediate}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="int-subject" className="fw-600 fs-13">Subject <span className="text-danger">*</span></label>
              <select 
                className="form-control" 
                id="int-subject"
                name="int_subject"
                value={formData.int_subject}
                onChange={handleInputChange}
              >
                <option value="">-- Select Subjects -- </option>
                <option value="Arts">Arts</option>
                <option value="Commerce">Commerce</option>
                <option value="Science PCM">Science PCM</option>
                <option value="Science PCB">Science PCB</option>
                <option value="Others">Others</option>
              </select>
            </div>

            <div className="mb-3 col-md-3">
              <label htmlFor="int-exam-year" className="fw-600 fs-13">Select Year<span className="text-danger">*</span></label>
              <DatePicker
                selected={intExamYearDate}
                onChange={(date) => {
                  setIntExamYearDate(date);
                  handleDateChange(date, 'int_exam_year');
                }}
                dateFormat="yyyy"
                placeholderText="Select Year"
                className="form-control"
                id="int-exam-year"
                name="int_exam_year"
                showYearDropdown
                showMonthDropdown={false}
                dropdownMode="select"
                yearDropdownItemNumber={50}
                scrollableYearDropdown
                maxDate={new Date()}
                isClearable
                required
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="int-university-board" className="fw-600 fs-13">Board/ University<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="int-university-board" 
                name="int_university_board" 
                placeholder="university|board" 
                value={formData.int_university_board}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="int-maximum-marks" className="fw-600 fs-13">Maxmimum Marks<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="int-maximum-marks" 
                name="int_maximum_marks" 
                placeholder="Maximum Marks" 
                value={formData.int_maximum_marks}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            <div className="mb-3 col-md-3">
              <label htmlFor="int-obtained-marks" className="fw-600 fs-13">Obtained Marks<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="int-obtained-marks" 
                name="int_obtained_marks" 
                placeholder="Obtained Marks" 
                value={formData.int_obtained_marks}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="int-grade" className="fw-600 fs-13">Percentage/ Grade<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="int-grade" 
                name="int_grade" 
                placeholder="percentage / Grade" 
                value={formData.int_grade}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="int-marksheetfile" className="fw-600 fs-13">Upload Marksheet<span className="text-danger">*</span></label>
              <input 
                type="file" 
                className="form-control" 
                id="int-marksheetfile" 
                name="int_marksheetfile" 
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
              {uploadedFileNames.int_marksheetfile && (
                <div className="mt-1">
                  {fileUploadStatus.int_marksheetfile.uploading && (
                    <small className="text-info">
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Uploading: {uploadedFileNames.int_marksheetfile}...
                    </small>
                  )}
                  {fileUploadStatus.int_marksheetfile.uploaded && (
                    <div className="mt-1">
                      <small className="text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        Uploaded: 
                        <a 
                          href={fileUploadStatus.int_marksheetfile.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-decoration-none ms-1"
                        >
                          {uploadedFileNames.int_marksheetfile}
                        </a>
                      </small>
                    </div>
                  )}
                  {fileUploadStatus.int_marksheetfile.error && (
                    <small className="text-danger">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      Error: {fileUploadStatus.int_marksheetfile.error}
                    </small>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="pt-4 row">
            <h5 className="p-2 col-12 fw-bold">Polytechnic Diploma / Other (If any)</h5>
            {/* Name */}
            <div className="mb-3 col-md-3">
              <label htmlFor="poly-intermediate" className="fw-600 fs-13">Polytechnic Diploma / Other<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="poly-intermediate" 
                name="poly_intermediate" 
                placeholder="Polytechnic Diploma | Other(If Any)" 
                value={formData.poly_intermediate}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="poly-course" className="fw-600 fs-13">Course <span className="text-danger">*</span></label>
              <select 
                className="form-control" 
                id="poly-course"
                name="poly_course"
                value={formData.poly_course}
                onChange={handleInputChange}
              >
                <option value="">-- Select Course -- </option>
                <option value="Diploma in Engineering">Diploma in Engineering</option>
                <option value="Diploma in Technology">Diploma in Technology</option>
                <option value="Diploma in Computer Science">Diploma in Computer Science</option>
                <option value="Diploma in Electronics">Diploma in Electronics</option>
                <option value="Diploma in Mechanical">Diploma in Mechanical</option>
                <option value="Diploma in Civil">Diploma in Civil</option>
                <option value="Diploma in Electrical">Diploma in Electrical</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="mb-3 col-md-3">
              <label htmlFor="poly-exam-year" className="fw-600 fs-13">Select Year<span className="text-danger">*</span></label>
              <DatePicker
                selected={polyExamYearDate}
                onChange={(date) => {
                  setPolyExamYearDate(date);
                  handleDateChange(date, 'poly_exam_year');
                }}
                dateFormat="yyyy"
                placeholderText="Select Year"
                className="form-control"
                id="poly-exam-year"
                name="poly_exam_year"
                showYearDropdown
                showMonthDropdown={false}
                dropdownMode="select"
                yearDropdownItemNumber={50}
                scrollableYearDropdown
                maxDate={new Date()}
                isClearable
                required
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="poly-university-board" className="fw-600 fs-13">Board/ University<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="poly-university-board" 
                name="poly_university_board" 
                placeholder="university|board" 
                value={formData.poly_university_board}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="poly-obtained-marks" className="fw-600 fs-13">Obtained Marks<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="poly-obtained-marks" 
                name="poly_obtained_marks" 
                placeholder="Obtained Marks" 
                value={formData.poly_obtained_marks}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="poly-maximum-marks" className="fw-600 fs-13">Maximum Marks<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="poly-maximum-marks" 
                name="poly_maximum_marks" 
                placeholder="Maximum Marks" 
                value={formData.poly_maximum_marks}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="poly-grade" className="fw-600 fs-13">Percentage/ Grade<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="poly-grade" 
                name="poly_grade" 
                placeholder="percentage / Grade" 
                value={formData.poly_grade}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="poly-marksheetfile" className="fw-600 fs-13">Upload Marksheet<span className="text-danger">*</span></label>
              <input 
                type="file" 
                className="form-control" 
                id="poly-marksheetfile" 
                name="poly_marksheetfile" 
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
              {uploadedFileNames.poly_marksheetfile && (
                <div className="mt-1">
                  {fileUploadStatus.poly_marksheetfile.uploading && (
                    <small className="text-info">
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Uploading: {uploadedFileNames.poly_marksheetfile}...
                    </small>
                  )}
                  {fileUploadStatus.poly_marksheetfile.uploaded && (
                    <div className="mt-1">
                      <small className="text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        Uploaded: 
                        <a 
                          href={fileUploadStatus.poly_marksheetfile.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-decoration-none ms-1"
                        >
                          {uploadedFileNames.poly_marksheetfile}
                        </a>
                      </small>
                    </div>
                  )}
                  {fileUploadStatus.poly_marksheetfile.error && (
                    <small className="text-danger">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      Error: {fileUploadStatus.poly_marksheetfile.error}
                    </small>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="pt-4 row">
            <h5 className="p-2 col-12 fw-bold">Graduation</h5>
            {/* Name */}
            <div className="mb-3 col-md-3">
              <label htmlFor="gradu-intermediate" className="fw-600 fs-13">Graduation<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="gradu-intermediate" 
                name="gradu_intermediate" 
                placeholder="Polytechnic Deploma | Other(If Any)" 
                value={formData.gradu_intermediate}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="gradu-course" className="fw-600 fs-13">Course <span className="text-danger">*</span></label>
              <select 
                className="form-control" 
                id="gradu-course"
                name="gradu_course"
                value={formData.gradu_course}
                onChange={handleInputChange}
              >
                <option value="">-- Select Course -- </option>
                <option value="ba">BA</option>
                <option value="Commerce">BCOM</option>
                <option value="BSC">BSC</option>
                <option value="BCA">BCA</option>
                <option value="BBA">BBA</option>
                <option value="BTECH">BTECH</option>
              </select>
            </div>

            <div className="mb-3 col-md-3">
              <label htmlFor="gradu-exam-year" className="fw-600 fs-13">Select Year<span className="text-danger">*</span></label>
              <DatePicker
                selected={graduExamYearDate}
                onChange={(date) => {
                  setGraduExamYearDate(date);
                  handleDateChange(date, 'gradu_exam_year');
                }}
                dateFormat="yyyy"
                placeholderText="Select Year"
                className="form-control"
                id="gradu-exam-year"
                name="gradu_exam_year"
                showYearDropdown
                showMonthDropdown={false}
                dropdownMode="select"
                yearDropdownItemNumber={50}
                scrollableYearDropdown
                maxDate={new Date()}
                isClearable
                required
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="gradu-university-board" className="fw-600 fs-13">Board/ University<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="gradu-university-board" 
                name="gradu_university_board" 
                placeholder="university|board" 
                value={formData.gradu_university_board}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="gradu-obtained-marks" className="fw-600 fs-13">Obtained Marks<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="gradu-obtained-marks" 
                name="gradu_obtained_marks" 
                placeholder="Obtained Marks" 
                value={formData.gradu_obtained_marks}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="gradu-maximum-marks" className="fw-600 fs-13">Maxmimum Marks<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="gradu-maximum-marks" 
                name="gradu_maximum_marks" 
                placeholder="Maximum Marks" 
                value={formData.gradu_maximum_marks}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="gradu-grade" className="fw-600 fs-13">Percentage/ Grade<span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="form-control" 
                id="gradu-grade" 
                name="gradu_grade" 
                placeholder="percentage / Grade" 
                value={formData.gradu_grade}
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3 col-md-3">
              <label htmlFor="gradu-marksheetfile" className="fw-600 fs-13">Upload Marksheet<span className="text-danger">*</span></label>
              <input 
                type="file" 
                className="form-control" 
                id="gradu-marksheetfile" 
                name="gradu_marksheetfile" 
                autoComplete="off" 
                required 
                onChange={handleInputChange}
              />
              {uploadedFileNames.gradu_marksheetfile && (
                <div className="mt-1">
                  {fileUploadStatus.gradu_marksheetfile.uploading && (
                    <small className="text-info">
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Uploading: {uploadedFileNames.gradu_marksheetfile}...
                    </small>
                  )}
                  {fileUploadStatus.gradu_marksheetfile.uploaded && (
                    <div className="mt-1">
                      <small className="text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        Uploaded: 
                        <a 
                          href={fileUploadStatus.gradu_marksheetfile.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-decoration-none ms-1"
                        >
                          {uploadedFileNames.gradu_marksheetfile}
                        </a>
                      </small>
                    </div>
                  )}
                  {fileUploadStatus.gradu_marksheetfile.error && (
                    <small className="text-danger">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      Error: {fileUploadStatus.gradu_marksheetfile.error}
                    </small>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 4: Document Upload Form Component
const Step4Form = ({ 
  formData, 
  handleInputChange, 
  step4Error, 
  uploadedFileNames, 
  fileUploadStatus,
  additionalDocuments,
  addAdditionalDocument,
  removeAdditionalDocument,
  updateDocumentName
}) => {
  // Document types configuration
  const documentTypes = [
    { id: 'docup_pp_photo', name: 'Passport Size Photo', required: true },
    { id: 'docup_std_sign', name: 'Student Signature', required: true },
    { id: 'docup_aadharcard', name: 'Aadhar Card', required: true },
    { id: 'docup_experince_emp', name: 'Experience Certificate/NOC (Present Employer)', required: true },
    { id: 'docup_migrate', name: 'Migration Certificate', required: true },
    { id: 'docup_otherdoc', name: 'Other Document (if any)', required: false }
  ];

  // Get status for a document
  const getDocumentStatus = (docId) => {
    if (!uploadedFileNames[docId]) {
      return { status: 'Not Uploaded', class: 'text-muted', icon: 'bi-dash-circle' };
    }
    
    // Check if file is currently uploading
    if (fileUploadStatus[docId]?.uploading) {
      return { status: 'Uploading...', class: 'text-info', icon: 'bi-arrow-clockwise' };
    }
    
    // Check if there was an upload error
    if (fileUploadStatus[docId]?.error) {
      return { status: 'Upload Failed', class: 'text-danger', icon: 'bi-x-circle' };
    }
    
    // Check if there was a metadata storage/update error
    if (fileUploadStatus[docId]?.metadataError) {
      return { status: 'Storage Failed', class: 'text-danger', icon: 'bi-exclamation-triangle' };
    }
    
    // Handle API document statuses
    if (fileUploadStatus[docId]?.uploaded && fileUploadStatus[docId]?.documentId) {
      const apiStatus = fileUploadStatus[docId].documentStatus;
      
      switch (apiStatus) {
        case 'verified':
          return { status: 'Verified', class: 'text-success', icon: 'bi-check-circle-fill' };
        case 'pending':
          return { status: 'Pending Review', class: 'text-warning', icon: 'bi-clock' };
        case 'rejected':
          return { status: 'Rejected', class: 'text-danger', icon: 'bi-x-circle-fill' };
        default:
          // Check if this was an update
          if (fileUploadStatus[docId]?.isUpdate) {
            return { status: 'Updated', class: 'text-success', icon: 'bi-check-circle' };
          } else {
            return { status: 'Stored', class: 'text-success', icon: 'bi-check-circle' };
          }
      }
    }
    
    // Uploaded but metadata not stored yet (shouldn't happen with immediate API calls)
    if (fileUploadStatus[docId]?.uploaded) {
      return { status: 'Processing...', class: 'text-warning', icon: 'bi-clock' };
    }
    
    return { status: 'Ready', class: 'text-primary', icon: 'bi-file-earmark' };
  };

  // Get comment for a document
  const getDocumentComment = (docId) => {
    // Show upload error if there is one
    if (fileUploadStatus[docId]?.error) {
      return fileUploadStatus[docId].error;
    }
    
    // Show metadata storage/update error if there is one
    if (fileUploadStatus[docId]?.metadataError) {
      return fileUploadStatus[docId].metadataError;
    }
    
    // Show rejection reason only if document is rejected (not verified)
    if (fileUploadStatus[docId]?.documentStatus === 'rejected' && fileUploadStatus[docId]?.rejectionReason) {
      return fileUploadStatus[docId].rejectionReason;
    }
    
    // For verified documents or documents without rejection reasons, show nothing
    return null;
  };

  return (
    <div className="tab">
      <div className="applic-wrap">
        <div id="step_4_form">
          {/* Step 4 Error Display */}
          {step4Error && (
            <div className="row mb-3">
              <div className="col-12">
                <div className={`alert ${step4Error.includes('successfully') ? 'alert-success' : 'alert-danger'}`} role="alert">
                  <div className="d-flex align-items-center">
                    <i className={`bi ${step4Error.includes('successfully') ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                    <div>
                      <strong>{step4Error.includes('successfully') ? 'Success:' : 'Error:'}</strong> {step4Error}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          
          <div className="pt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold mb-0">Document Upload</h5>
              <button 
                type="button" 
                className="btn btn-outline-primary btn-sm"
                onClick={addAdditionalDocument}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Add Document
              </button>
            </div>

            {/* Document Upload Table */}
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th width="25%">Document Type</th>
                    <th width="30%">File Upload</th>
                    <th width="15%">Approval Status</th>
                    <th width="20%">Comment</th>
                    <th width="10%">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documentTypes.map((docType, index) => {
                    const statusInfo = getDocumentStatus(docType.id);
                    const comment = getDocumentComment(docType.id);
                    
                    return (
                      <tr key={docType.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="fw-semibold">{docType.name}</span>
                            {docType.required && <span className="text-danger ms-1">*</span>}
                          </div>
                        </td>
                        <td>
                          <div>
                            <input 
                              type="file" 
                              className="form-control form-control-sm" 
                              id={`docup-${docType.id}`}
                              name={docType.id} 
                              accept="image/*,.pdf" 
                              autoComplete="off" 
                              required={docType.required}
                              disabled={fileUploadStatus[docType.id]?.documentStatus === 'verified'}
                              onChange={handleInputChange}
                            />
                            {fileUploadStatus[docType.id]?.documentStatus === 'verified' && (
                              <div className="mt-1">
                                <small className="text-success">
                                  <i className="bi bi-shield-check me-1"></i>
                                  Document verified - Cannot be updated
                                </small>
                              </div>
                            )}
                            {uploadedFileNames[docType.id] && (
                              <div className="mt-1">
                                <small className="text-success">
                                  <i className="fas fa-check-circle me-1"></i>
                                  <a 
                                    href={fileUploadStatus[docType.id]?.url || '#'} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-decoration-none"
                                  >
                                    {uploadedFileNames[docType.id]}
                                  </a>
                                </small>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${statusInfo.class === 'text-muted' ? 'bg-secondary' : 
                                                      statusInfo.class === 'text-warning' ? 'bg-warning' : 
                                                      statusInfo.class === 'text-info' ? 'bg-info' :
                                                      statusInfo.class === 'text-success' ? 'bg-success' : 'bg-danger'}`}>
                            <i className={`bi ${statusInfo.icon} me-1`}></i>
                            {statusInfo.status}
                          </span>
                        </td>
                        <td>
                          {comment ? (
                            <div className={comment.includes('Failed') || comment.includes('Error') ? 'text-danger' : 
                                           comment.includes('reason') || comment.includes('quality') || comment.includes('clearer') ? 'text-warning' : 'text-muted'}>
                              <small>
                                <i className={`bi ${comment.includes('Failed') || comment.includes('Error') ? 'bi-exclamation-triangle' : 
                                               comment.includes('reason') || comment.includes('quality') || comment.includes('clearer') ? 'bi-exclamation-triangle' : 'bi-info-circle'} me-1`}></i>
                                {comment}
                              </small>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm" role="group">
                            {uploadedFileNames[docType.id] && (
                              <>
                                <button 
                                  type="button" 
                                  className="btn btn-outline-info btn-sm"
                                  title="View Document"
                                  onClick={() => {
                                    if (fileUploadStatus[docType.id]?.url) {
                                      window.open(fileUploadStatus[docType.id].url, '_blank');
                                    }
                                  }}
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  
                  {/* Additional Documents Rows */}
                  {additionalDocuments.map((additionalDoc) => {
                    const statusInfo = getDocumentStatus(additionalDoc.id);
                    const comment = getDocumentComment(additionalDoc.id);
                    
                    return (
                      <tr key={additionalDoc.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <input
                              type="text"
                              className="form-control form-control-sm me-2"
                              placeholder="Document name"
                              value={additionalDoc.name}
                              onChange={(e) => updateDocumentName(additionalDoc.id, e.target.value)}
                            />
                            <span className="text-muted">(Additional)</span>
                          </div>
                        </td>
                        <td>
                          <div>
                            <input 
                              type="file" 
                              className="form-control form-control-sm" 
                              id={`docup-${additionalDoc.id}`}
                              name={additionalDoc.id} 
                              accept="image/*,.pdf" 
                              autoComplete="off"
                              disabled={fileUploadStatus[additionalDoc.id]?.documentStatus === 'verified'}
                              onChange={handleInputChange}
                            />
                            {fileUploadStatus[additionalDoc.id]?.documentStatus === 'verified' && (
                              <div className="mt-1">
                                <small className="text-success">
                                  <i className="bi bi-shield-check me-1"></i>
                                  Document verified - Cannot be updated
                                </small>
                              </div>
                            )}
                            {uploadedFileNames[additionalDoc.id] && (
                              <div className="mt-1">
                                <small className="text-success">
                                  <i className="fas fa-check-circle me-1"></i>
                                  <a 
                                    href={fileUploadStatus[additionalDoc.id]?.url || '#'} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-decoration-none"
                                  >
                                    {uploadedFileNames[additionalDoc.id]}
                                  </a>
                                </small>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${statusInfo.class === 'text-muted' ? 'bg-secondary' : 
                                                      statusInfo.class === 'text-warning' ? 'bg-warning' : 
                                                      statusInfo.class === 'text-info' ? 'bg-info' :
                                                      statusInfo.class === 'text-success' ? 'bg-success' : 'bg-danger'}`}>
                            <i className={`bi ${statusInfo.icon} me-1`}></i>
                            {statusInfo.status}
                          </span>
                        </td>
                        <td>
                          {comment ? (
                            <div className={comment.includes('Failed') || comment.includes('Error') ? 'text-danger' : 
                                           comment.includes('reason') || comment.includes('quality') || comment.includes('clearer') ? 'text-warning' : 'text-muted'}>
                              <small>
                                <i className={`bi ${comment.includes('Failed') || comment.includes('Error') ? 'bi-exclamation-triangle' : 
                                               comment.includes('reason') || comment.includes('quality') || comment.includes('clearer') ? 'bi-exclamation-triangle' : 'bi-info-circle'} me-1`}></i>
                                {comment}
                              </small>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm" role="group">
                            {uploadedFileNames[additionalDoc.id] && (
                              <button 
                                type="button" 
                                className="btn btn-outline-info btn-sm"
                                title="View Document"
                                onClick={() => {
                                  if (fileUploadStatus[additionalDoc.id]?.url) {
                                    window.open(fileUploadStatus[additionalDoc.id].url, '_blank');
                                  }
                                }}
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Additional Documents Section */}
            <div className="mt-4">
              <h6 className="fw-semibold mb-3">Additional Documents</h6>
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                You can add more documents using the "Add Document" button above. All documents will be submitted for review.
              </div>
            </div>

            {/* Upload Status Summary */}
            <div className="mt-4">
              <h6 className="fw-semibold mb-3">Upload Status Summary</h6>
              <div className="row">
                <div className="col-md-3">
                  <div className="card border-success">
                    <div className="card-body text-center">
                      <i className="bi bi-check-circle text-success fs-2"></i>
                      <h5 className="card-title mt-2">Uploaded</h5>
                      <p className="card-text text-muted">Documents successfully uploaded</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-info">
                    <div className="card-body text-center">
                      <i className="bi bi-arrow-clockwise text-info fs-2"></i>
                      <h5 className="card-title mt-2">Uploading</h5>
                      <p className="card-text text-muted">Documents currently uploading</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-danger">
                    <div className="card-body text-center">
                      <i className="bi bi-x-circle text-danger fs-2"></i>
                      <h5 className="card-title mt-2">Failed</h5>
                      <p className="card-text text-muted">Documents with upload errors</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-secondary">
                    <div className="card-body text-center">
                      <i className="bi bi-dash-circle text-secondary fs-2"></i>
                      <h5 className="card-title mt-2">Not Uploaded</h5>
                      <p className="card-text text-muted">Documents not yet uploaded</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;
